import React, { useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Button,
  TextField,
  Stack,
  IconButton,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  SelectChangeEvent,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SendIcon from "@mui/icons-material/Send";
import GavelIcon from "@mui/icons-material/Gavel";
import BusinessIcon from "@mui/icons-material/Business";
import FolderIcon from "@mui/icons-material/Folder";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import PublicIcon from "@mui/icons-material/Public";
import GroupsIcon from "@mui/icons-material/Groups";
import styles from "./mou-orchestrator.module.scss";
import MarkdownRenderer from "../../MarkdownRenderer";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { sendMessageToAgent } from "../../../store/userSlice";
import { v4 as uuid } from "uuid";

type MouOrchestratorAgentProps = {
  parsedContent: any;
  messageId?: string;
  isWorkflowCompleted?: boolean; // True when document is generated (STAGE_DOWNLOAD)
  hasUserResponded?: boolean; // True when user has already submitted from this message
};

const MOU_AGENT_ID = 54;
const MOU_ENDPOINT = "/stream/mou";

// Jurisdiction options
const JURISDICTION_OPTIONS = [
  "UAE",
  "United States",
  "United Kingdom",
  "Singapore",
  "Saudi Arabia",
  "India",
  "Germany",
  "Other",
];

// Helper to detect if this is an intake question
function isIntakeQuestion(question?: string): boolean {
  if (!question) return false;
  const lower = question.toLowerCase();
  // Check for the new intake format with numbered items
  const hasCounterparty = lower.includes("counterparty") || lower.includes("counter party");
  const hasProject = lower.includes("project") || lower.includes("joint venture") || lower.includes("collaboration");
  const hasPurpose = lower.includes("purpose") || lower.includes("scope") || lower.includes("intend to achieve");
  const hasJurisdiction = lower.includes("jurisdiction") || lower.includes("governing law");

  // New format: "1. **Counterparty**" style numbered list
  const hasNumberedFormat = lower.includes("1.") && lower.includes("2.") && lower.includes("3.");

  return hasCounterparty && hasProject && (hasJurisdiction || hasPurpose || hasNumberedFormat);
}

// Helper to detect if this is a confirmation question
function isConfirmationQuestion(question?: string, response?: string): boolean {
  if (!question) return false;
  const lowerQ = question.toLowerCase();
  const hasConfirmKeyword = lowerQ.includes("confirm") || lowerQ.includes("review");
  // Check for extracted data patterns in response
  const hasExtractedData =
    response?.includes("**Counterparty:**") ||
    response?.includes("**Project:**") ||
    response?.includes("**Parties:**") ||
    response?.includes("**Purpose and Scope:**") ||
    response?.includes("**Recommended Template:**") ||
    response?.includes("**Selected Template:**");
  return hasConfirmKeyword && hasExtractedData;
}

// Helper to detect if this is a template selection question
function isTemplateSelectionQuestion(question?: string, response?: string): boolean {
  if (!question && !response) return false;
  const text = (question || "") + (response || "");
  const lower = text.toLowerCase();
  const hasTemplateChoice =
    (lower.includes("short-form") || lower.includes("short form")) &&
    (lower.includes("long-form") || lower.includes("long form"));
  const hasSelectionPrompt =
    lower.includes("which template") ||
    lower.includes("select the template") ||
    lower.includes("type 'short'") ||
    lower.includes("type 'long'");
  return hasTemplateChoice && hasSelectionPrompt;
}

// ============================================================================
// PLAYBOOK & SOURCES PARSING UTILITIES
// ============================================================================

interface ParsedMessageContent {
  mainContent: string;
  playbook: string | null;
  sources: string[] | null;
  sourcesNotConfigured: boolean;
  sharePointUrl: string | null;
}

/**
 * Parse message content to extract Playbook, Sources consulted, and SharePoint links
 * for distinct rendering as citation blocks and action buttons.
 */
function parsePlaybookAndSources(message: string): ParsedMessageContent {
  // Strip PDF-related lines the backend may include
  const cleanedMessage = message
    .split("\n")
    .filter(
      (line) =>
        !/\[download\s+pdf\]/i.test(line) &&
        !/pdf\s+unavailable/i.test(line)
    )
    .join("\n");
  let mainContent = cleanedMessage;
  let playbook: string | null = null;
  let sources: string[] | null = null;
  let sourcesNotConfigured = false;
  let sharePointUrl: string | null = null;

  // Extract **Playbook:** line
  const playbookMatch = message.match(/\*\*Playbook:\*\*\s*(.+?)(?:\n|$)/);
  if (playbookMatch) {
    playbook = playbookMatch[1].trim();
    mainContent = mainContent.replace(playbookMatch[0], "").trim();
  }

  // Check for "not configured" playbook message
  const notConfiguredMatch = message.match(
    /_Playbook index not configured.*?_/
  );
  if (notConfiguredMatch) {
    sourcesNotConfigured = true;
    mainContent = mainContent.replace(notConfiguredMatch[0], "").trim();
  }

  // Extract **Sources consulted:** section with bullet list
  const sourcesMatch = message.match(
    /\*\*Sources consulted:\*\*\s*\n((?:[-•]\s*.+\n?)+)/
  );
  if (sourcesMatch) {
    const sourcesText = sourcesMatch[1];
    sources = sourcesText
      .split("\n")
      .map((line) => line.replace(/^[-•]\s*/, "").trim())
      .filter((line) => line.length > 0);
    mainContent = mainContent.replace(sourcesMatch[0], "").trim();
  }

  // Extract SharePoint link: 📁 [View in SharePoint](url) or [View in SharePoint](url)
  const sharePointMatch = message.match(
    /📁?\s*\[View in SharePoint\]\((https?:\/\/[^\s)]+)\)/i
  );
  if (sharePointMatch) {
    sharePointUrl = sharePointMatch[1];
    mainContent = mainContent.replace(sharePointMatch[0], "").trim();
  }

  // Clean up any double newlines left behind
  mainContent = mainContent.replace(/\n{3,}/g, "\n\n").trim();

  return { mainContent, playbook, sources, sourcesNotConfigured, sharePointUrl };
}

/**
 * Citation block component for displaying Playbook or Sources
 */
function CitationBlock({
  playbook,
  sources,
  sourcesNotConfigured,
}: {
  playbook?: string | null;
  sources?: string[] | null;
  sourcesNotConfigured?: boolean;
}): JSX.Element | null {
  if (!playbook && !sources && !sourcesNotConfigured) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        backgroundColor: "#f5f5f5",
        borderLeft: "3px solid #9e9e9e",
        borderRadius: "4px",
      }}
    >
      {playbook && (
        <Box sx={{ mb: sources ? 1.5 : 0 }}>
          <Typography
            variant="caption"
            sx={{
              color: "#616161",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              display: "block",
              mb: 0.5,
            }}
          >
            Playbook
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#424242", fontFamily: "monospace", fontSize: "13px" }}
          >
            {playbook}
          </Typography>
        </Box>
      )}

      {sources && sources.length > 0 && (
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: "#616161",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              display: "block",
              mb: 0.5,
            }}
          >
            Sources Consulted
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {sources.map((source, idx) => (
              <Typography
                key={idx}
                component="li"
                variant="body2"
                sx={{ color: "#424242", fontSize: "13px", mb: 0.25 }}
              >
                {source}
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      {sourcesNotConfigured && !playbook && !sources && (
        <Typography
          variant="body2"
          sx={{ color: "#757575", fontStyle: "italic", fontSize: "13px" }}
        >
          Playbook index not configured — draft generated from G42 standard
          positions.
        </Typography>
      )}
    </Box>
  );
}

// G42 Constants (matching backend)
const G42_LEGAL_NAME = "G FORTY TWO GENERAL TRADING LLC, a limited liability company incorporated in the Emirate of Abu Dhabi, UAE";
const G42_DESCRIPTION = "G42 is an artificial intelligence and technology conglomerate principally operating in the UAE and internationally.";

// Parse extracted data from markdown response
function parseExtractedData(responseText: string): {
  counterparty: string;
  counterpartyAddress: string;
  counterpartyDescription: string;
  project: string;
  purposeAndScope: string;
  jurisdiction: string;
  parties: string[];
  objectives: string[];
  additionalTerms: string;
  selectedTemplate: string;
  firstPartyName: string;
  firstPartyLegalName: string;
  firstPartyDescription: string;
  secondPartyName: string;
  secondPartyAddress: string;
  secondPartyDescription: string;
  g42InitialInvestment: string;
  g42AdditionalInvestment: string;
  g42NonMonetaryContribution: string;
  counterpartyInvestment: string;
  counterpartyNonMonetaryContribution: string;
  counterpartySignatoryName: string;
  counterpartySignatoryTitle: string;
  hasJointVentureSection: boolean;
} {
  const result = {
    counterparty: "",
    counterpartyAddress: "",
    counterpartyDescription: "",
    project: "",
    purposeAndScope: "",
    jurisdiction: "",
    parties: [] as string[],
    objectives: [] as string[],
    additionalTerms: "",
    selectedTemplate: "",
    firstPartyName: "G42",
    firstPartyLegalName: G42_LEGAL_NAME,
    firstPartyDescription: G42_DESCRIPTION,
    secondPartyName: "",
    secondPartyAddress: "",
    secondPartyDescription: "",
    g42InitialInvestment: "",
    g42AdditionalInvestment: "",
    g42NonMonetaryContribution: "",
    counterpartyInvestment: "",
    counterpartyNonMonetaryContribution: "",
    counterpartySignatoryName: "",
    counterpartySignatoryTitle: "",
    hasJointVentureSection: false,
  };

  // Extract selected template (shown after template selection step)
  const selectedTemplateMatch = responseText.match(/\*\*Selected Template:\*\*\s*([^\n]+)/);
  if (selectedTemplateMatch) result.selectedTemplate = selectedTemplateMatch[1].trim();

  // Extract First Party info
  const firstPartyMatch = responseText.match(/\*\*First Party:\*\*\s*([^\n]+)/);
  if (firstPartyMatch) result.firstPartyName = firstPartyMatch[1].trim();

  // Extract Second Party info
  const secondPartyMatch = responseText.match(/\*\*Second Party:\*\*\s*([^\n]+)/);
  if (secondPartyMatch) result.secondPartyName = secondPartyMatch[1].trim();

  // Extract Second Party Address
  const secondPartyAddressMatch = responseText.match(/_Address:_\s*([^\n]+)/);
  if (secondPartyAddressMatch) result.secondPartyAddress = secondPartyAddressMatch[1].trim();

  // Extract Second Party Description
  const secondPartyDescMatch = responseText.match(/_Description:_\s*([^\n]+)/);
  if (secondPartyDescMatch) result.secondPartyDescription = secondPartyDescMatch[1].trim();

  // Extract parties (legacy format)
  const partiesMatch = responseText.match(/\*\*Parties:\*\*\s*([^\n]+)/);
  if (partiesMatch) {
    result.parties = partiesMatch[1].split(",").map((p) => p.trim());
  }

  // Extract counterparty
  const counterpartyMatch = responseText.match(/\*\*Counterparty:\*\*\s*([^\n]+)/);
  if (counterpartyMatch) result.counterparty = counterpartyMatch[1].trim();

  // Extract counterparty address
  const addressMatch = responseText.match(/\*\*Counterparty Address:\*\*\s*([^\n]+)/);
  if (addressMatch) result.counterpartyAddress = addressMatch[1].trim();

  // Extract counterparty description
  const counterpartyDescMatch = responseText.match(/\*\*Counterparty Description:\*\*\s*([^\n]+)/);
  if (counterpartyDescMatch) result.counterpartyDescription = counterpartyDescMatch[1].trim();

  // Extract project / joint venture / collaboration
  const projectMatch = responseText.match(/\*\*Project\s*\/?\s*Joint Venture\s*\/?\s*Collaboration:\*\*\s*([^\n]+)/);
  if (projectMatch) {
    result.project = projectMatch[1].trim();
  } else {
    // Fallback to just **Project:**
    const simpleProjectMatch = responseText.match(/\*\*Project:\*\*\s*([^\n]+)/);
    if (simpleProjectMatch) result.project = simpleProjectMatch[1].trim();
  }

  // Extract purpose and scope (can be multiline)
  const purposeMatch = responseText.match(/\*\*Purpose and Scope:\*\*\s*([\s\S]*?)(?=\n\*\*Jurisdiction|\n\*\*Key Objectives|\n\*\*Additional|\n\*\*Recommended|$)/);
  if (purposeMatch) {
    result.purposeAndScope = purposeMatch[1].trim();
  }

  // Extract jurisdiction
  const jurisdictionMatch = responseText.match(/\*\*Jurisdiction:\*\*\s*([^\n]+)/);
  if (jurisdictionMatch) result.jurisdiction = jurisdictionMatch[1].trim();

  // Extract key objectives (bullet list with • or -)
  const objectivesMatch = responseText.match(/\*\*Key Objectives:\*\*\s*([\s\S]*?)(?=\n\*\*Additional|\n\*\*Recommended|\n\n\*\*|$)/);
  if (objectivesMatch) {
    const objectivesText = objectivesMatch[1];
    const bullets = objectivesText.match(/[•\-\*]\s*([^\n]+)/g);
    if (bullets) {
      result.objectives = bullets.map((b) => b.replace(/^[•\-\*]\s*/, "").trim());
    }
  }

  // Extract additional terms
  // Stops at "---" too: the JOINT VENTURE CONTRIBUTIONS section (when present) immediately
  // follows Additional Terms behind a "---" divider, and would otherwise get swallowed here.
  const termsMatch = responseText.match(/\*\*Additional Terms:\*\*\s*([\s\S]*?)(?=\n\n\*\*Recommended|\n\*\*Recommended|\n---|$)/);
  if (termsMatch) {
    result.additionalTerms = termsMatch[1].trim();
  } else {
    // Fallback to Additional Notes
    const notesMatch = responseText.match(/\*\*Additional Notes:\*\*\s*([^\n]+(?:\n(?!\*\*)[^\n]+)*)/);
    if (notesMatch) result.additionalTerms = notesMatch[1].trim();
  }

  // Extract Joint Venture Contributions section (Long-Form/JV MoUs only — heading is
  // omitted entirely, not shown blank, whenever there's no JV data at all).
  // Real format (confirmed against backend's _build_confirmation_message() output):
  //   **JOINT VENTURE CONTRIBUTIONS**
  //
  //   **G42:** {initial}; additional: {additional}   <- "; additional: ..." omitted if empty
  //     {non-monetary}                                <- whole line omitted if empty
  //
  //   **{counterparty name}:** {investment}           <- label is the counterparty's actual name
  //     {non-monetary}                                <- whole line omitted if empty
  //
  //   **Counterparty Signatory:** {name} ({title})    <- "(title)" omitted if empty
  // Primary values show as "_Not specified_" when empty; that maps back to "".
  const jvSectionMatch = responseText.match(/\*\*JOINT VENTURE CONTRIBUTIONS\*\*([\s\S]*?)(?=\n---|$)/i);
  if (jvSectionMatch) {
    result.hasJointVentureSection = true;

    const cleanJvValue = (value: string): string => {
      const trimmed = value.trim();
      return /^_?not specified_?$/i.test(trimmed) ? "" : trimmed;
    };

    const jvLines = jvSectionMatch[1]
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    const g42LineIndex = jvLines.findIndex((line) => /^\*\*G42:\*\*/i.test(line));
    if (g42LineIndex !== -1) {
      const g42Value = jvLines[g42LineIndex].replace(/^\*\*G42:\*\*\s*/i, "");
      const [initialPart, additionalPart] = g42Value.split(/;\s*additional:\s*/i);
      result.g42InitialInvestment = cleanJvValue(initialPart || "");
      if (additionalPart) result.g42AdditionalInvestment = cleanJvValue(additionalPart);

      const nextLine = jvLines[g42LineIndex + 1];
      if (nextLine && !nextLine.startsWith("**")) {
        result.g42NonMonetaryContribution = cleanJvValue(nextLine);
      }
    }

    // Counterparty investment line: the bold line after G42's (whatever the label —
    // it's the counterparty's actual name, not a fixed "Counterparty:" label).
    const counterpartyLineIndex = jvLines.findIndex(
      (line, idx) =>
        idx > g42LineIndex &&
        /^\*\*(.+?):\*\*/.test(line) &&
        !/^\*\*Counterparty Signatory:\*\*/i.test(line)
    );
    if (counterpartyLineIndex !== -1) {
      const counterpartyMatch = jvLines[counterpartyLineIndex].match(/^\*\*(.+?):\*\*\s*(.*)$/);
      if (counterpartyMatch) {
        result.counterpartyInvestment = cleanJvValue(counterpartyMatch[2] || "");
      }
      const nextLine = jvLines[counterpartyLineIndex + 1];
      if (nextLine && !nextLine.startsWith("**")) {
        result.counterpartyNonMonetaryContribution = cleanJvValue(nextLine);
      }
    }

    const signatoryLineIndex = jvLines.findIndex((line) => /^\*\*Counterparty Signatory:\*\*/i.test(line));
    if (signatoryLineIndex !== -1) {
      const signatoryValue = jvLines[signatoryLineIndex].replace(/^\*\*Counterparty Signatory:\*\*\s*/i, "");
      const titleMatch = signatoryValue.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
      if (titleMatch) {
        result.counterpartySignatoryName = cleanJvValue(titleMatch[1]);
        result.counterpartySignatoryTitle = cleanJvValue(titleMatch[2]);
      } else {
        result.counterpartySignatoryName = cleanJvValue(signatoryValue);
      }
    }
  }

  return result;
}

export default function MouOrchestratorAgent({
  parsedContent,
  messageId,
  isWorkflowCompleted = false,
  hasUserResponded = false,
}: MouOrchestratorAgentProps): JSX.Element {
  const dispatch = useAppDispatch();
  const currentAgent = useSelector(
    (state: RootState) => state.agents.currentAgent
  );

  // Extract data from parsedContent - handle multiple nesting levels
  const data = parsedContent?.finalAnswer || parsedContent;
  const result = data?.result?.data || data?.result || data;
  const message = result?.message || data?.message || "";
  const status = result?.status || data?.status || "";
  const stage = result?.stage || data?.stage || ""; // New: workflow stage (inline_edit, etc.)
  const question = data?.question || result?.question || "";
  const responseRaw = data?.response || result?.response;
  const responseText = Array.isArray(responseRaw)
    ? responseRaw.join("\n\n")
    : responseRaw || "";

  // DEBUG: Log all extracted values
  console.log("[MouOrchestratorAgent] parsedContent:", parsedContent);
  console.log("[MouOrchestratorAgent] extracted data:", data);
  console.log("[MouOrchestratorAgent] result:", result);
  console.log("[MouOrchestratorAgent] question:", question);
  console.log("[MouOrchestratorAgent] responseText:", responseText);
  console.log("[MouOrchestratorAgent] status:", status);
  console.log("[MouOrchestratorAgent] stage:", stage);
  console.log("[MouOrchestratorAgent] isIntakeQuestion:", isIntakeQuestion(question));

  // Helper to send message to MoU agent
  const handleSendMessage = (msg: string) => {
    dispatch(
      sendMessageToAgent({
        agentId: currentAgent?.id || MOU_AGENT_ID,
        message: msg,
        questionId: uuid(),
        timestamp: new Date().toISOString(),
        agentEndpoint: MOU_ENDPOINT,
        sessionId: null,
      })
    );
  };

  // ============================================================================
  // VIEW ROUTING based on response data shape
  // ============================================================================

  // HITL partial intake: response array has mou_intake_agent result → pre-filled form with missing-field notice
  const responseItems = Array.isArray(data?.response) ? data.response : null;
  const intakePrefill = responseItems?.find((item: any) => item.agent_id === "mou_intake_agent")?.result || null;
  if (question && intakePrefill) {
    console.log("[MouOrchestratorAgent] Routing to IntakeFormView (pre-filled)");
    return <IntakeFormView data={data} prefill={intakePrefill} onSend={handleSendMessage} isLocked={isWorkflowCompleted} hasUserResponded={hasUserResponded} />;
  }

  // HITL Conversation with intake question - show form
  // Check both data.question and the extracted question variable
  if (question && isIntakeQuestion(question)) {
    console.log("[MouOrchestratorAgent] Routing to IntakeFormView");
    return <IntakeFormView data={data} onSend={handleSendMessage} isLocked={isWorkflowCompleted} hasUserResponded={hasUserResponded} />;
  }

  // STAGE: inline_edit - Show field editing form between template selections
  // This is Step 4 in the new workflow: Intake → Entity Verification → Template Selection → Inline Edit → Template Selection → Download
  if (stage === "inline_edit") {
    console.log("[MouOrchestratorAgent] Routing to InlineEditView (stage: inline_edit)");
    return <InlineEditView data={data} responseText={responseText} onSend={handleSendMessage} isLocked={isWorkflowCompleted} hasUserResponded={hasUserResponded} />;
  }

  // HITL Template selection question - show toggle buttons
  if (isTemplateSelectionQuestion(question, responseText)) {
    console.log("[MouOrchestratorAgent] Routing to TemplateSelectionView");
    return <TemplateSelectionView data={data} responseText={responseText} onSend={handleSendMessage} isLocked={isWorkflowCompleted} hasUserResponded={hasUserResponded} />;
  }

  // HITL Confirmation question - show structured review card
  if (question && isConfirmationQuestion(question, responseText)) {
    console.log("[MouOrchestratorAgent] Routing to ReviewConfirmationView");
    return <ReviewConfirmationView data={data} responseText={responseText} onSend={handleSendMessage} isLocked={isWorkflowCompleted} hasUserResponded={hasUserResponded} />;
  }

  // HITL Conversation - generic question (not intake or confirmation)
  if (question) {
    console.log("[MouOrchestratorAgent] Routing to ConversationView");
    return <ConversationView data={data} onSend={handleSendMessage} isLocked={isWorkflowCompleted} hasUserResponded={hasUserResponded} />;
  }

  // Intake Summary View - mou_intake_agent result with extracted fields
  if (result?.counterparty_name) {
    return <IntakeSummaryView result={result} isLocked={isWorkflowCompleted} />;
  }

  // Confirmation View - awaiting_confirmation status with confirmation data
  if (
    status === "awaiting_confirmation" ||
    data?.counterparty ||
    data?.project_name ||
    data?.template_type
  ) {
    return (
      <ConfirmationView
        data={data}
        result={result}
        onSend={handleSendMessage}
        isLocked={isWorkflowCompleted}
        hasUserResponded={hasUserResponded}
      />
    );
  }

  // Draft Ready View - completed status with download URL
  if (status === "completed" && (data?.download_url || result?.download_url)) {
    return <DraftReadyView data={data} result={result} />;
  }

  // Intake Prompt View - initial prompt asking for details (fallback)
  if (
    status === "awaiting_intake" ||
    message?.toLowerCase().includes("counterparty") ||
    message?.toLowerCase().includes("project name") ||
    message?.toLowerCase().includes("collaboration")
  ) {
    return <IntakePromptView data={data} message={message} />;
  }

  // Fallback - show message or raw content
  return <FallbackView data={data} message={message} />;
}

// ============================================================================
// DOCUMENT PREVIEW COMPONENT - Shows preview of the MoU document
// ============================================================================
function DocumentPreview({
  templateType,
  firstParty,
  firstPartyLegalName,
  firstPartyDescription,
  secondParty,
  secondPartyAddress,
  secondPartyDescription,
  project,
  purposeAndScope,
  jurisdiction,
  objectives,
  additionalTerms,
}: {
  templateType: string;
  firstParty: string;
  firstPartyLegalName: string;
  firstPartyDescription: string;
  secondParty: string;
  secondPartyAddress: string;
  secondPartyDescription: string;
  project: string;
  purposeAndScope: string;
  jurisdiction: string;
  objectives: string[];
  additionalTerms: string;
}): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(false);

  const isLongForm = templateType?.toLowerCase().includes("long");

  return (
    <div className={styles.documentPreview}>
      <div
        className={styles.documentPreviewHeader}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className={styles.documentPreviewHeaderLeft}>
          <DescriptionIcon sx={{ fontSize: 20, color: "#004141" }} />
          <Typography className={styles.documentPreviewTitle}>
            Document Preview
          </Typography>
          <Chip
            label={isLongForm ? "Long-Form MoU" : "Short-Form MoU"}
            size="small"
            sx={{
              fontSize: "10px",
              height: "20px",
              backgroundColor: "#f0fbf9",
              color: "#004141",
              border: "1px solid #b2e5de",
              ml: 1,
            }}
          />
        </div>
        <IconButton size="small">
          {isExpanded ? (
            <ExpandLessIcon sx={{ color: "#004141" }} />
          ) : (
            <ExpandMoreIcon sx={{ color: "#004141" }} />
          )}
        </IconButton>
      </div>

      {isExpanded && (
        <div className={styles.documentPreviewContent}>
          <div className={styles.documentPage}>
            {/* Document Title */}
            <div className={styles.docTitle}>
              MEMORANDUM OF UNDERSTANDING
            </div>
            <div className={styles.docSubtitle}>
              {project || "[Project Name]"}
            </div>

            {/* Parties Section */}
            <div className={styles.docSection}>
              <div className={styles.docSectionTitle}>PARTIES</div>
              <div className={styles.docParagraph}>
                This Memorandum of Understanding ("MoU") is entered into by and between:
              </div>
              <div className={styles.docParty}>
                <strong>1. {firstParty}</strong>
                <br />
                <span className={styles.docPartyDetails}>{firstPartyLegalName}</span>
                <br />
                <span className={styles.docPartyDesc}>{firstPartyDescription}</span>
              </div>
              <div className={styles.docAnd}>AND</div>
              <div className={styles.docParty}>
                <strong>2. {secondParty || "[Counterparty Name]"}</strong>
                {secondPartyAddress && (
                  <>
                    <br />
                    <span className={styles.docPartyDetails}>Address: {secondPartyAddress}</span>
                  </>
                )}
                {secondPartyDescription && (
                  <>
                    <br />
                    <span className={styles.docPartyDesc}>{secondPartyDescription}</span>
                  </>
                )}
              </div>
              <div className={styles.docParagraph}>
                (collectively referred to as the "Parties" and individually as a "Party")
              </div>
            </div>

            {/* Purpose and Scope */}
            <div className={styles.docSection}>
              <div className={styles.docSectionTitle}>PURPOSE AND SCOPE</div>
              <div className={styles.docParagraph}>
                {purposeAndScope || "[Purpose and scope of the collaboration will be detailed here]"}
              </div>
            </div>

            {/* Key Objectives */}
            {objectives.length > 0 && (
              <div className={styles.docSection}>
                <div className={styles.docSectionTitle}>KEY OBJECTIVES</div>
                <div className={styles.docParagraph}>
                  The Parties agree to collaborate on the following objectives:
                </div>
                <ul className={styles.docList}>
                  {objectives.map((obj, idx) => (
                    <li key={idx}>{obj}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Additional sections for Long-Form */}
            {isLongForm && (
              <>
                <div className={styles.docSection}>
                  <div className={styles.docSectionTitle}>GOVERNANCE</div>
                  <div className={styles.docParagraph}>
                    [Governance structure and steering committee details will be included here]
                  </div>
                </div>
                <div className={styles.docSection}>
                  <div className={styles.docSectionTitle}>INTELLECTUAL PROPERTY</div>
                  <div className={styles.docParagraph}>
                    [IP ownership, licensing, and protection provisions will be detailed here]
                  </div>
                </div>
              </>
            )}

            {/* Additional Terms */}
            {additionalTerms && (
              <div className={styles.docSection}>
                <div className={styles.docSectionTitle}>ADDITIONAL TERMS</div>
                <div className={styles.docParagraph}>{additionalTerms}</div>
              </div>
            )}

            {/* Governing Law */}
            <div className={styles.docSection}>
              <div className={styles.docSectionTitle}>GOVERNING LAW</div>
              <div className={styles.docParagraph}>
                This MoU shall be governed by and construed in accordance with the laws of {jurisdiction || "UAE"}.
              </div>
            </div>

            {/* Signatures */}
            <div className={styles.docSection}>
              <div className={styles.docSectionTitle}>SIGNATURES</div>
              <div className={styles.docSignatures}>
                <div className={styles.docSignatureBlock}>
                  <div className={styles.docSignatureLine}>________________________</div>
                  <div className={styles.docSignatureName}>For {firstParty}</div>
                  <div className={styles.docSignatureRole}>Authorized Signatory</div>
                </div>
                <div className={styles.docSignatureBlock}>
                  <div className={styles.docSignatureLine}>________________________</div>
                  <div className={styles.docSignatureName}>For {secondParty || "[Counterparty]"}</div>
                  <div className={styles.docSignatureRole}>Authorized Signatory</div>
                </div>
              </div>
            </div>

            {/* Preview Note */}
            <div className={styles.docPreviewNote}>
              <InfoOutlinedIcon sx={{ fontSize: 14 }} />
              <span>This is a preview. The final document will include complete legal language and formatting.</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// REVIEW CONFIRMATION VIEW - Parsed structured confirmation card
// ============================================================================
function ReviewConfirmationView({
  data,
  responseText,
  onSend,
  isLocked = false,
  hasUserResponded = false,
}: {
  data: any;
  responseText: string;
  onSend: (msg: string) => void;
  isLocked?: boolean;
  hasUserResponded?: boolean;
}): JSX.Element {
  // Disable all interactions if locked OR user has already responded
  const isDisabled = isLocked || hasUserResponded;
  const [copied, setCopied] = useState(false);

  // Parse the extracted data from response
  const extracted = parseExtractedData(responseText);

  console.log("[MouOrchestratorAgent] ReviewConfirmationView - extracted:", extracted);

  // Check if extracted jurisdiction is in standard options, otherwise use "Other"
  const extractedJurisdiction = extracted.jurisdiction || "";
  const isJurisdictionInOptions = JURISDICTION_OPTIONS.includes(extractedJurisdiction);
  const initialJurisdiction = isJurisdictionInOptions ? extractedJurisdiction : (extractedJurisdiction ? "Other" : "");
  const initialCustomJurisdiction = isJurisdictionInOptions ? "" : extractedJurisdiction;

  // Editable state for all fields
  const [editedFields, setEditedFields] = useState({
    parties: extracted.parties.join(", "),
    counterparty: extracted.counterparty || extracted.secondPartyName,
    counterpartyAddress: extracted.counterpartyAddress || extracted.secondPartyAddress,
    counterpartyDescription: extracted.counterpartyDescription || extracted.secondPartyDescription,
    project: extracted.project,
    purposeAndScope: extracted.purposeAndScope,
    jurisdiction: initialJurisdiction,
    objectives: extracted.objectives.join("\n"),
    additionalTerms: extracted.additionalTerms,
    g42InitialInvestment: extracted.g42InitialInvestment,
    g42AdditionalInvestment: extracted.g42AdditionalInvestment,
    g42NonMonetaryContribution: extracted.g42NonMonetaryContribution,
    counterpartyInvestment: extracted.counterpartyInvestment,
    counterpartyNonMonetaryContribution: extracted.counterpartyNonMonetaryContribution,
    counterpartySignatoryName: extracted.counterpartySignatoryName,
    counterpartySignatoryTitle: extracted.counterpartySignatoryTitle,
  });

  // State for custom jurisdiction when "Other" is selected
  const [customJurisdiction, setCustomJurisdiction] = useState(initialCustomJurisdiction);

  // Track which fields are in edit mode
  const [editingField, setEditingField] = useState<string | null>(null);

  // Check if any field has been modified
  const originalCounterparty = extracted.counterparty || extracted.secondPartyName;
  const originalAddress = extracted.counterpartyAddress || extracted.secondPartyAddress;
  const originalDescription = extracted.counterpartyDescription || extracted.secondPartyDescription;

  const hasChanges =
    editedFields.parties !== extracted.parties.join(", ") ||
    editedFields.counterparty !== originalCounterparty ||
    editedFields.counterpartyAddress !== originalAddress ||
    editedFields.counterpartyDescription !== originalDescription ||
    editedFields.project !== extracted.project ||
    editedFields.purposeAndScope !== extracted.purposeAndScope ||
    editedFields.jurisdiction !== initialJurisdiction ||
    customJurisdiction !== initialCustomJurisdiction ||
    editedFields.objectives !== extracted.objectives.join("\n") ||
    editedFields.additionalTerms !== extracted.additionalTerms ||
    editedFields.g42InitialInvestment !== extracted.g42InitialInvestment ||
    editedFields.g42AdditionalInvestment !== extracted.g42AdditionalInvestment ||
    editedFields.g42NonMonetaryContribution !== extracted.g42NonMonetaryContribution ||
    editedFields.counterpartyInvestment !== extracted.counterpartyInvestment ||
    editedFields.counterpartyNonMonetaryContribution !== extracted.counterpartyNonMonetaryContribution ||
    editedFields.counterpartySignatoryName !== extracted.counterpartySignatoryName ||
    editedFields.counterpartySignatoryTitle !== extracted.counterpartySignatoryTitle;

  const handleFieldChange = (field: string, value: string) => {
    setEditedFields((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditClick = (field: string) => {
    setEditingField(field);
  };

  const handleSaveField = () => {
    setEditingField(null);
  };

  const handleCancelEdit = (field: string) => {
    // Reset to original value
    const originalValues: Record<string, string> = {
      parties: extracted.parties.join(", "),
      counterparty: originalCounterparty,
      counterpartyAddress: originalAddress,
      counterpartyDescription: originalDescription,
      project: extracted.project,
      purposeAndScope: extracted.purposeAndScope,
      jurisdiction: initialJurisdiction,
      objectives: extracted.objectives.join("\n"),
      additionalTerms: extracted.additionalTerms,
      g42InitialInvestment: extracted.g42InitialInvestment,
      g42AdditionalInvestment: extracted.g42AdditionalInvestment,
      g42NonMonetaryContribution: extracted.g42NonMonetaryContribution,
      counterpartyInvestment: extracted.counterpartyInvestment,
      counterpartyNonMonetaryContribution: extracted.counterpartyNonMonetaryContribution,
      counterpartySignatoryName: extracted.counterpartySignatoryName,
      counterpartySignatoryTitle: extracted.counterpartySignatoryTitle,
    };
    setEditedFields((prev) => ({ ...prev, [field]: originalValues[field] }));
    // Reset customJurisdiction when cancelling jurisdiction edit
    if (field === "jurisdiction") {
      setCustomJurisdiction(initialCustomJurisdiction);
    }
    setEditingField(null);
  };

  const handleSubmitChanges = () => {
    // Get the actual jurisdiction value (use customJurisdiction when "Other" is selected)
    const actualJurisdiction = editedFields.jurisdiction === "Other" ? customJurisdiction : editedFields.jurisdiction;

    // Format the edited data as a correction message
    let correctionMessage = `Here are my corrections:

Counterparty Name: ${editedFields.counterparty}
Counterparty Description: ${editedFields.counterpartyDescription}
Counterparty Address: ${editedFields.counterpartyAddress}
Project / Joint Venture / Collaboration: ${editedFields.project}
Purpose and Scope: ${editedFields.purposeAndScope}
Jurisdiction: ${actualJurisdiction}
Key Objectives:
${editedFields.objectives.split("\n").map((o) => `- ${o.trim()}`).join("\n")}
Additional Terms: ${editedFields.additionalTerms}`;

    // Include Joint Venture Contributions only if this MoU actually has that section
    if (extracted.hasJointVentureSection) {
      const jvFields: Array<[string, string]> = [
        ["G42 Initial Investment", editedFields.g42InitialInvestment],
        ["G42 Additional/Milestone Investment", editedFields.g42AdditionalInvestment],
        ["G42 Non-Monetary Contribution", editedFields.g42NonMonetaryContribution],
        ["Counterparty Investment", editedFields.counterpartyInvestment],
        ["Counterparty Non-Monetary Contribution", editedFields.counterpartyNonMonetaryContribution],
        ["Counterparty Signatory Name", editedFields.counterpartySignatoryName],
        ["Counterparty Signatory Title", editedFields.counterpartySignatoryTitle],
      ];
      correctionMessage += `\n\nJoint Venture Contribution Details:\n${jvFields.map(([label, value]) => `${label}: ${value}`).join("\n")}`;
    }

    onSend(correctionMessage);
  };

  const handleCopy = () => {
    const text = `${extracted.selectedTemplate ? `Selected Template: ${extracted.selectedTemplate}\n` : ""}First Party: G42
${G42_LEGAL_NAME}

Counterparty: ${editedFields.counterparty}
Counterparty Address: ${editedFields.counterpartyAddress}
Counterparty Description: ${editedFields.counterpartyDescription}

Project: ${editedFields.project}
Purpose and Scope: ${editedFields.purposeAndScope}
Jurisdiction: ${editedFields.jurisdiction}
Key Objectives: ${editedFields.objectives}
Additional Terms: ${editedFields.additionalTerms}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={styles.container} style={isDisabled ? { opacity: 0.7, pointerEvents: "none" } : undefined}>
      {/* Header */}
      <div className={styles.confirmationHeader}>
        <div className={styles.confirmationHeaderLeft}>
          <Typography className={styles.confirmationEyebrow}>
            REVIEW & CONFIRM
          </Typography>
          <Typography className={styles.confirmationTitle}>
            MoU Details Extracted
          </Typography>
        </div>
        <div className={styles.confirmationHeaderRight}>
          {isDisabled ? (
            <Chip
              icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
              label="Submitted"
              size="small"
              sx={{
                backgroundColor: "#d1fae5",
                color: "#065f46",
                fontWeight: 500,
                "& .MuiChip-icon": { color: "#059669" },
              }}
            />
          ) : (
            <div className={styles.confirmationBadge}>
              <span className={styles.confirmationBadgeText}>Step 2 of 3</span>
            </div>
          )}
          <Tooltip title={copied ? "Copied!" : "Copy details"}>
            <IconButton size="small" onClick={handleCopy} disabled={isDisabled}>
              <ContentCopyIcon sx={{ fontSize: 16, color: "#004141" }} />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.confirmationBody}>
        {/* Selected Template Banner - shown after template selection */}
        {extracted.selectedTemplate && (
          <div className={styles.selectedTemplateBanner}>
            <div className={styles.selectedTemplateIcon}>
              <DescriptionIcon sx={{ fontSize: 20, color: "#00a888" }} />
            </div>
            <div className={styles.selectedTemplateContent}>
              <Typography className={styles.selectedTemplateLabel}>
                Selected Template
              </Typography>
              <Typography className={styles.selectedTemplateValue}>
                {extracted.selectedTemplate}
              </Typography>
            </div>
            <Button
              variant="outlined"
              size="small"
              onClick={() => onSend("change template")}
              sx={{
                textTransform: "none",
                borderColor: "#b2e5de",
                color: "#004141",
                fontSize: "12px",
                "&:hover": { borderColor: "#00a888", backgroundColor: "#f0fbf9" },
              }}
            >
              Change Template
            </Button>
          </div>
        )}

        {/* PARTIES Section */}
        <div className={styles.partiesSection}>
          <Typography className={styles.partiesSectionTitle}>
            <GroupsIcon sx={{ fontSize: 18, color: "#004141", mr: 1 }} />
            PARTIES
          </Typography>

          <div className={styles.partiesGrid}>
            {/* First Party (G42 - Read-only) */}
            <div className={styles.partyCard}>
              <Typography className={styles.partyLabel}>First Party</Typography>
              <Typography className={styles.partyName}>G42</Typography>
              <Typography className={styles.partyLegalName}>{G42_LEGAL_NAME}</Typography>
              <Typography className={styles.partyDescription}>{G42_DESCRIPTION}</Typography>
            </div>

            {/* Second Party (Counterparty - Editable) */}
            <div className={styles.partyCard}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography className={styles.partyLabel}>Second Party</Typography>
                {editingField !== "counterparty" && (
                  <IconButton size="small" onClick={() => handleEditClick("counterparty")}>
                    <EditIcon sx={{ fontSize: 14, color: "#6b7280" }} />
                  </IconButton>
                )}
              </Box>
              {editingField === "counterparty" ? (
                <Box sx={{ mt: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Name"
                    value={editedFields.counterparty}
                    onChange={(e) => handleFieldChange("counterparty", e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Address"
                    value={editedFields.counterpartyAddress}
                    onChange={(e) => handleFieldChange("counterpartyAddress", e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Description"
                    multiline
                    rows={2}
                    value={editedFields.counterpartyDescription}
                    onChange={(e) => handleFieldChange("counterpartyDescription", e.target.value)}
                  />
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Button size="small" variant="outlined" onClick={() => handleCancelEdit("counterparty")}>Cancel</Button>
                    <Button size="small" variant="contained" onClick={handleSaveField} sx={{ backgroundColor: "#00a888" }}>Save</Button>
                  </Stack>
                </Box>
              ) : (
                <>
                  <Typography
                    className={styles.partyName}
                    sx={{ backgroundColor: editedFields.counterparty !== originalCounterparty ? "#fff4e6" : "transparent", px: editedFields.counterparty !== originalCounterparty ? 1 : 0, borderRadius: 1 }}
                  >
                    {editedFields.counterparty || "Not specified"}
                  </Typography>
                  {editedFields.counterpartyAddress && (
                    <Typography className={styles.partyAddress}>
                      <em>Address:</em> {editedFields.counterpartyAddress}
                    </Typography>
                  )}
                  {editedFields.counterpartyDescription && (
                    <Typography className={styles.partyDescription}>
                      <em>Description:</em> {editedFields.counterpartyDescription}
                    </Typography>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* MoU Details Section */}
        <Typography className={styles.mouDetailsSectionTitle}>
          <DescriptionIcon sx={{ fontSize: 18, color: "#004141", mr: 1 }} />
          MoU DETAILS
        </Typography>

        {/* Key Details Grid */}
        <div className={styles.confirmationGrid}>
          {/* Project */}
          <div className={styles.confirmationCard}>
            <div className={styles.confirmationCardIcon}>
              <FolderIcon sx={{ fontSize: 20, color: "#00a888" }} />
            </div>
            <div className={styles.confirmationCardContent}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography className={styles.confirmationCardLabel}>
                  Project / Joint Venture
                </Typography>
                {editingField !== "project" && (
                  <IconButton size="small" onClick={() => handleEditClick("project")}>
                    <EditIcon sx={{ fontSize: 16, color: "#6b7280" }} />
                  </IconButton>
                )}
              </Box>
              {editingField === "project" ? (
                <Box sx={{ mt: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={editedFields.project}
                    onChange={(e) => handleFieldChange("project", e.target.value)}
                    placeholder="Name of the initiative"
                  />
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Button size="small" variant="outlined" onClick={() => handleCancelEdit("project")}>Cancel</Button>
                    <Button size="small" variant="contained" onClick={handleSaveField} sx={{ backgroundColor: "#00a888" }}>Save</Button>
                  </Stack>
                </Box>
              ) : (
                <Typography
                  className={styles.confirmationCardValue}
                  sx={{ backgroundColor: editedFields.project !== extracted.project ? "#fff4e6" : "transparent", px: editedFields.project !== extracted.project ? 1 : 0, borderRadius: 1 }}
                >
                  {editedFields.project || "Not specified"}
                </Typography>
              )}
            </div>
          </div>

          {/* Jurisdiction */}
          <div className={styles.confirmationCard}>
            <div className={styles.confirmationCardIcon}>
              <GavelIcon sx={{ fontSize: 20, color: "#00a888" }} />
            </div>
            <div className={styles.confirmationCardContent}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography className={styles.confirmationCardLabel}>
                  Jurisdiction
                </Typography>
                {editingField !== "jurisdiction" && (
                  <IconButton size="small" onClick={() => handleEditClick("jurisdiction")}>
                    <EditIcon sx={{ fontSize: 16, color: "#6b7280" }} />
                  </IconButton>
                )}
              </Box>
              {editingField === "jurisdiction" ? (
                <Box sx={{ mt: 1 }}>
                  <FormControl fullWidth size="small">
                    <Select
                      value={editedFields.jurisdiction}
                      onChange={(e) => {
                        handleFieldChange("jurisdiction", e.target.value);
                        // Clear customJurisdiction when switching away from "Other"
                        if (e.target.value !== "Other") {
                          setCustomJurisdiction("");
                        }
                      }}
                    >
                      {JURISDICTION_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {/* Show text input when "Other" is selected */}
                  {editedFields.jurisdiction === "Other" && (
                    <TextField
                      fullWidth
                      placeholder="Enter jurisdiction (e.g., Kingdom of Saudi Arabia)"
                      value={customJurisdiction}
                      onChange={(e) => setCustomJurisdiction(e.target.value)}
                      size="small"
                      variant="outlined"
                      error={!customJurisdiction.trim()}
                      helperText={!customJurisdiction.trim() ? "Please specify the jurisdiction" : ""}
                      sx={{ mt: 1.5 }}
                    />
                  )}
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Button size="small" variant="outlined" onClick={() => handleCancelEdit("jurisdiction")}>Cancel</Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleSaveField}
                      disabled={editedFields.jurisdiction === "Other" && !customJurisdiction.trim()}
                      sx={{ backgroundColor: "#00a888", "&:disabled": { backgroundColor: "#ccc" } }}
                    >
                      Save
                    </Button>
                  </Stack>
                </Box>
              ) : (
                <Typography
                  className={styles.confirmationCardValue}
                  sx={{ backgroundColor: editedFields.jurisdiction !== initialJurisdiction || customJurisdiction !== initialCustomJurisdiction ? "#fff4e6" : "transparent", px: editedFields.jurisdiction !== initialJurisdiction || customJurisdiction !== initialCustomJurisdiction ? 1 : 0, borderRadius: 1 }}
                >
                  {editedFields.jurisdiction === "Other" ? customJurisdiction || "Other" : editedFields.jurisdiction || "UAE"}
                </Typography>
              )}
            </div>
          </div>
        </div>

        {/* Purpose and Scope */}
        <div className={styles.confirmationSection}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography className={styles.confirmationSectionLabel}>
              Purpose and Scope
            </Typography>
            {editingField !== "purposeAndScope" && (
              <IconButton size="small" onClick={() => handleEditClick("purposeAndScope")}>
                <EditIcon sx={{ fontSize: 16, color: "#6b7280" }} />
              </IconButton>
            )}
          </Box>
          {editingField === "purposeAndScope" ? (
            <Box sx={{ mt: 1 }}>
              <TextField
                fullWidth
                multiline
                rows={4}
                size="small"
                value={editedFields.purposeAndScope}
                onChange={(e) => handleFieldChange("purposeAndScope", e.target.value)}
                placeholder="What the parties intend to achieve together"
              />
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button size="small" variant="outlined" onClick={() => handleCancelEdit("purposeAndScope")}>Cancel</Button>
                <Button size="small" variant="contained" onClick={handleSaveField} sx={{ backgroundColor: "#00a888" }}>Save</Button>
              </Stack>
            </Box>
          ) : (
            <div
              className={styles.confirmationSectionContent}
              style={{ backgroundColor: editedFields.purposeAndScope !== extracted.purposeAndScope ? "#fff4e6" : undefined }}
            >
              <Typography className={styles.confirmationSectionText}>
                {editedFields.purposeAndScope || "Not specified"}
              </Typography>
            </div>
          )}
        </div>

        {/* Key Objectives */}
        <div className={styles.confirmationSection}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography className={styles.confirmationSectionLabel}>
              Key Objectives
            </Typography>
            {editingField !== "objectives" && (
              <IconButton size="small" onClick={() => handleEditClick("objectives")}>
                <EditIcon sx={{ fontSize: 16, color: "#6b7280" }} />
              </IconButton>
            )}
          </Box>
          {editingField === "objectives" ? (
            <Box sx={{ mt: 1 }}>
              <TextField
                fullWidth
                multiline
                rows={4}
                size="small"
                value={editedFields.objectives}
                onChange={(e) => handleFieldChange("objectives", e.target.value)}
                placeholder="Enter each objective on a new line"
                helperText="One objective per line"
              />
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button size="small" variant="outlined" onClick={() => handleCancelEdit("objectives")}>Cancel</Button>
                <Button size="small" variant="contained" onClick={handleSaveField} sx={{ backgroundColor: "#00a888" }}>Save</Button>
              </Stack>
            </Box>
          ) : (
            <div
              className={styles.objectivesList}
              style={{ backgroundColor: editedFields.objectives !== extracted.objectives.join("\n") ? "#fff4e6" : undefined, padding: editedFields.objectives !== extracted.objectives.join("\n") ? "8px" : undefined, borderRadius: "4px" }}
            >
              {editedFields.objectives ? (
                editedFields.objectives.split("\n").filter((o) => o.trim()).map((obj, idx) => (
                  <div key={idx} className={styles.objectiveItem}>
                    <span className={styles.objectiveBullet}>•</span>
                    <Typography className={styles.objectiveText}>{obj.trim()}</Typography>
                  </div>
                ))
              ) : (
                <Typography className={styles.confirmationSectionText}>No objectives specified</Typography>
              )}
            </div>
          )}
        </div>

        {/* Additional Terms */}
        <div className={styles.confirmationSection}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography className={styles.confirmationSectionLabel}>
              Additional Terms
            </Typography>
            {editingField !== "additionalTerms" && (
              <IconButton size="small" onClick={() => handleEditClick("additionalTerms")}>
                <EditIcon sx={{ fontSize: 16, color: "#6b7280" }} />
              </IconButton>
            )}
          </Box>
          {editingField === "additionalTerms" ? (
            <Box sx={{ mt: 1 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                size="small"
                value={editedFields.additionalTerms}
                onChange={(e) => handleFieldChange("additionalTerms", e.target.value)}
                placeholder="Duration, special requirements, or specific provisions"
              />
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button size="small" variant="outlined" onClick={() => handleCancelEdit("additionalTerms")}>Cancel</Button>
                <Button size="small" variant="contained" onClick={handleSaveField} sx={{ backgroundColor: "#00a888" }}>Save</Button>
              </Stack>
            </Box>
          ) : (
            <div
              className={styles.confirmationSectionContent}
              style={{ backgroundColor: editedFields.additionalTerms !== extracted.additionalTerms ? "#fff4e6" : undefined }}
            >
              <Typography className={styles.confirmationSectionText}>
                {editedFields.additionalTerms || "No additional terms specified"}
              </Typography>
            </div>
          )}
        </div>

        {/* Joint Venture Contributions (optional — Long-Form/JV MoUs only) */}
        {extracted.hasJointVentureSection && (
          <div className={styles.confirmationSection}>
            <Typography className={styles.confirmationSectionLabel} sx={{ fontWeight: 700, mb: 1 }}>
              Joint Venture Contributions
            </Typography>

            {(
              [
                ["g42InitialInvestment", "G42 Initial Investment"],
                ["g42AdditionalInvestment", "G42 Additional/Milestone Investment"],
                ["g42NonMonetaryContribution", "G42 Non-Monetary Contribution"],
                ["counterpartyInvestment", "Counterparty Investment"],
                ["counterpartyNonMonetaryContribution", "Counterparty Non-Monetary Contribution"],
                ["counterpartySignatoryName", "Counterparty Signatory Name"],
                ["counterpartySignatoryTitle", "Counterparty Signatory Title"],
              ] as const
            ).map(([fieldKey, label]) => (
              <Box key={fieldKey} sx={{ mt: 1.5 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography className={styles.confirmationSectionLabel}>{label}</Typography>
                  {editingField !== fieldKey && !isDisabled && (
                    <IconButton size="small" onClick={() => handleEditClick(fieldKey)}>
                      <EditIcon sx={{ fontSize: 16, color: "#6b7280" }} />
                    </IconButton>
                  )}
                </Box>
                {editingField === fieldKey ? (
                  <Box sx={{ mt: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={editedFields[fieldKey]}
                      onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                    />
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <Button size="small" variant="outlined" onClick={() => handleCancelEdit(fieldKey)}>Cancel</Button>
                      <Button size="small" variant="contained" onClick={handleSaveField} sx={{ backgroundColor: "#00a888" }}>Save</Button>
                    </Stack>
                  </Box>
                ) : (
                  <div
                    className={styles.confirmationSectionContent}
                    style={{ backgroundColor: editedFields[fieldKey] !== extracted[fieldKey] ? "#fff4e6" : undefined }}
                  >
                    <Typography className={styles.confirmationSectionText}>
                      {editedFields[fieldKey] || "Not specified"}
                    </Typography>
                  </div>
                )}
              </Box>
            ))}
          </div>
        )}

        {/* Document Preview Section */}
        <DocumentPreview
          templateType={extracted.selectedTemplate}
          firstParty="G42"
          firstPartyLegalName={G42_LEGAL_NAME}
          firstPartyDescription={G42_DESCRIPTION}
          secondParty={editedFields.counterparty}
          secondPartyAddress={editedFields.counterpartyAddress}
          secondPartyDescription={editedFields.counterpartyDescription}
          project={editedFields.project}
          purposeAndScope={editedFields.purposeAndScope}
          jurisdiction={editedFields.jurisdiction}
          objectives={editedFields.objectives.split("\n").filter((o) => o.trim())}
          additionalTerms={editedFields.additionalTerms}
        />

      </div>

      {/* Footer with Actions */}
      <div className={styles.confirmationFooter}>
        <div className={styles.confirmationHint}>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: "#6b7280" }} />
          <Typography className={styles.confirmationHintText}>
            Review the details above. Click on any field to edit, or confirm to proceed with draft generation.
          </Typography>
        </div>
        <div className={styles.confirmationActions}>
          {hasChanges && (
            <Chip
              label="You have unsaved changes"
              size="small"
              sx={{
                backgroundColor: "#fff4e6",
                color: "#b45309",
                border: "1px solid #fbbf24",
                mr: 1,
              }}
            />
          )}
          {hasChanges ? (
            <Button
              variant="contained"
              startIcon={<SendIcon sx={{ fontSize: 16 }} />}
              size="small"
              onClick={handleSubmitChanges}
              disabled={isDisabled}
              sx={{
                textTransform: "none",
                backgroundColor: "#f59e0b",
                "&:hover": { backgroundColor: "#d97706" },
                "&:disabled": { backgroundColor: "#ccc" },
              }}
            >
              Submit Changes for Review
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
              size="small"
              onClick={() => onSend("confirm")}
              disabled={isDisabled}
              sx={{
                textTransform: "none",
                backgroundColor: "#00a888",
                "&:hover": { backgroundColor: "#00947a" },
                "&:disabled": { backgroundColor: "#ccc" },
              }}
            >
              Confirm & Generate Draft
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// INLINE EDIT VIEW - Step 4: Edit fields after initial template selection
// This is the new STAGE_INLINE_EDIT step in the workflow
// ============================================================================
function InlineEditView({
  data,
  responseText,
  onSend,
  isLocked = false,
  hasUserResponded = false,
}: {
  data: any;
  responseText: string;
  onSend: (msg: string) => void;
  isLocked?: boolean;
  hasUserResponded?: boolean;
}): JSX.Element {
  // Disable all interactions if locked OR user has already responded
  const isDisabled = isLocked || hasUserResponded;

  // Parse the extracted data from response
  const extracted = parseExtractedData(responseText);

  // Editable state for all fields
  const [editedFields, setEditedFields] = useState({
    parties: extracted.parties.join(", "),
    counterparty: extracted.counterparty || extracted.secondPartyName,
    counterpartyAddress: extracted.counterpartyAddress || extracted.secondPartyAddress,
    counterpartyDescription: extracted.counterpartyDescription || extracted.secondPartyDescription,
    project: extracted.project,
    purposeAndScope: extracted.purposeAndScope,
    jurisdiction: extracted.jurisdiction,
    objectives: extracted.objectives.join("\n"),
    additionalTerms: extracted.additionalTerms,
  });

  // Track which fields are in edit mode
  const [editingField, setEditingField] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Original values for comparison
  const originalCounterparty = extracted.counterparty || extracted.secondPartyName;
  const originalAddress = extracted.counterpartyAddress || extracted.secondPartyAddress;
  const originalDescription = extracted.counterpartyDescription || extracted.secondPartyDescription;

  const handleFieldChange = (field: string, value: string) => {
    setEditedFields((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditClick = (field: string) => {
    if (isDisabled) return;
    setEditingField(field);
  };

  const handleSaveField = () => {
    setEditingField(null);
  };

  const handleCancelEdit = (field: string) => {
    // Reset to original value
    const originalValues: Record<string, string> = {
      parties: extracted.parties.join(", "),
      counterparty: originalCounterparty,
      counterpartyAddress: originalAddress,
      counterpartyDescription: originalDescription,
      project: extracted.project,
      purposeAndScope: extracted.purposeAndScope,
      jurisdiction: extracted.jurisdiction,
      objectives: extracted.objectives.join("\n"),
      additionalTerms: extracted.additionalTerms,
    };
    setEditedFields((prev) => ({ ...prev, [field]: originalValues[field] }));
    setEditingField(null);
  };

  const handleSubmitEdits = () => {
    if (isDisabled) return;
    setIsSubmitting(true);

    // Build structured JSON object with all fields (not just changed ones for inline edit)
    const editedData: Record<string, string | string[]> = {
      counterparty_name: editedFields.counterparty,
      counterparty_address: editedFields.counterpartyAddress,
      counterparty_description: editedFields.counterpartyDescription,
      project_name: editedFields.project,
      collaboration_summary: editedFields.purposeAndScope,
      jurisdiction: editedFields.jurisdiction,
      additional_notes: editedFields.additionalTerms,
      key_objectives: editedFields.objectives
        .split("\n")
        .map((o) => o.trim())
        .filter((o) => o.length > 0),
    };

    // Serialize as JSON string and send
    const jsonPayload = JSON.stringify(editedData);
    onSend(jsonPayload);
  };

  return (
    <div className={styles.container} style={isDisabled ? { opacity: 0.7, pointerEvents: "none" } : undefined}>
      {/* Header */}
      <div className={styles.confirmationHeader}>
        <div className={styles.confirmationHeaderLeft}>
          <Typography className={styles.confirmationEyebrow}>
            EDIT DETAILS
          </Typography>
          <Typography className={styles.confirmationTitle}>
            Review & Edit MoU Information
          </Typography>
        </div>
        <div className={styles.confirmationHeaderRight}>
          {isDisabled ? (
            <Chip
              icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
              label="Submitted"
              size="small"
              sx={{
                backgroundColor: "#d1fae5",
                color: "#065f46",
                fontWeight: 500,
                "& .MuiChip-icon": { color: "#059669" },
              }}
            />
          ) : (
            <div className={styles.confirmationBadge}>
              <span className={styles.confirmationBadgeText}>Step 4 of 6</span>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.confirmationBody}>
        {/* Info Banner */}
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: "8px",
            backgroundColor: "#f0f9ff",
            border: "1px solid #0ea5e9",
            display: "flex",
            gap: 1,
            alignItems: "flex-start",
          }}
        >
          <InfoOutlinedIcon sx={{ color: "#0ea5e9", fontSize: 18, mt: "2px", flexShrink: 0 }} />
          <Typography sx={{ fontSize: "13px", color: "#0369a1", lineHeight: 1.5 }}>
            Review and edit the extracted information below. Click on any field to modify it, then save your changes to confirm the template selection.
          </Typography>
        </Box>

        {/* PARTIES Section */}
        <div className={styles.partiesSection}>
          <Typography className={styles.partiesSectionTitle}>
            <GroupsIcon sx={{ fontSize: 18, color: "#004141", mr: 1 }} />
            PARTIES
          </Typography>

          <div className={styles.partiesGrid}>
            {/* First Party (G42 - Read-only) */}
            <div className={styles.partyCard}>
              <Typography className={styles.partyCardLabel}>First Party</Typography>
              <Typography className={styles.partyCardName}>G42</Typography>
              <Typography className={styles.partyCardLegalName}>{G42_LEGAL_NAME}</Typography>
            </div>

            {/* Counterparty (Editable) */}
            <div className={styles.partyCard}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography className={styles.partyCardLabel}>Counterparty</Typography>
                {editingField !== "counterparty" && !isDisabled && (
                  <IconButton size="small" onClick={() => handleEditClick("counterparty")}>
                    <EditIcon sx={{ fontSize: 14, color: "#6b7280" }} />
                  </IconButton>
                )}
              </Box>
              {editingField === "counterparty" ? (
                <Box sx={{ mt: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={editedFields.counterparty}
                    onChange={(e) => handleFieldChange("counterparty", e.target.value)}
                  />
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Button size="small" variant="outlined" onClick={() => handleCancelEdit("counterparty")}>Cancel</Button>
                    <Button size="small" variant="contained" onClick={handleSaveField} sx={{ backgroundColor: "#00a888" }}>Save</Button>
                  </Stack>
                </Box>
              ) : (
                <Typography
                  className={styles.partyCardName}
                  sx={{ backgroundColor: editedFields.counterparty !== originalCounterparty ? "#fff4e6" : "transparent", px: editedFields.counterparty !== originalCounterparty ? 1 : 0, borderRadius: 1 }}
                >
                  {editedFields.counterparty}
                </Typography>
              )}
            </div>
          </div>
        </div>

        {/* Project Details Section */}
        <div className={styles.confirmationSection}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography className={styles.confirmationSectionLabel}>
              Project / Collaboration
            </Typography>
            {editingField !== "project" && !isDisabled && (
              <IconButton size="small" onClick={() => handleEditClick("project")}>
                <EditIcon sx={{ fontSize: 16, color: "#6b7280" }} />
              </IconButton>
            )}
          </Box>
          {editingField === "project" ? (
            <Box sx={{ mt: 1 }}>
              <TextField
                fullWidth
                size="small"
                value={editedFields.project}
                onChange={(e) => handleFieldChange("project", e.target.value)}
              />
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button size="small" variant="outlined" onClick={() => handleCancelEdit("project")}>Cancel</Button>
                <Button size="small" variant="contained" onClick={handleSaveField} sx={{ backgroundColor: "#00a888" }}>Save</Button>
              </Stack>
            </Box>
          ) : (
            <div
              className={styles.confirmationSectionContent}
              style={{ backgroundColor: editedFields.project !== extracted.project ? "#fff4e6" : undefined }}
            >
              <Typography className={styles.confirmationSectionText}>
                {editedFields.project || "Not specified"}
              </Typography>
            </div>
          )}
        </div>

        {/* Purpose and Scope Section */}
        <div className={styles.confirmationSection}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography className={styles.confirmationSectionLabel}>
              Purpose and Scope
            </Typography>
            {editingField !== "purposeAndScope" && !isDisabled && (
              <IconButton size="small" onClick={() => handleEditClick("purposeAndScope")}>
                <EditIcon sx={{ fontSize: 16, color: "#6b7280" }} />
              </IconButton>
            )}
          </Box>
          {editingField === "purposeAndScope" ? (
            <Box sx={{ mt: 1 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                size="small"
                value={editedFields.purposeAndScope}
                onChange={(e) => handleFieldChange("purposeAndScope", e.target.value)}
              />
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button size="small" variant="outlined" onClick={() => handleCancelEdit("purposeAndScope")}>Cancel</Button>
                <Button size="small" variant="contained" onClick={handleSaveField} sx={{ backgroundColor: "#00a888" }}>Save</Button>
              </Stack>
            </Box>
          ) : (
            <div
              className={styles.confirmationSectionContent}
              style={{ backgroundColor: editedFields.purposeAndScope !== extracted.purposeAndScope ? "#fff4e6" : undefined }}
            >
              <Typography className={styles.confirmationSectionText}>
                {editedFields.purposeAndScope || "Not specified"}
              </Typography>
            </div>
          )}
        </div>

        {/* Jurisdiction Section */}
        <div className={styles.confirmationSection}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography className={styles.confirmationSectionLabel}>
              Jurisdiction
            </Typography>
            {editingField !== "jurisdiction" && !isDisabled && (
              <IconButton size="small" onClick={() => handleEditClick("jurisdiction")}>
                <EditIcon sx={{ fontSize: 16, color: "#6b7280" }} />
              </IconButton>
            )}
          </Box>
          {editingField === "jurisdiction" ? (
            <Box sx={{ mt: 1 }}>
              <FormControl fullWidth size="small">
                <Select
                  value={editedFields.jurisdiction}
                  onChange={(e) => handleFieldChange("jurisdiction", e.target.value)}
                >
                  {JURISDICTION_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button size="small" variant="outlined" onClick={() => handleCancelEdit("jurisdiction")}>Cancel</Button>
                <Button size="small" variant="contained" onClick={handleSaveField} sx={{ backgroundColor: "#00a888" }}>Save</Button>
              </Stack>
            </Box>
          ) : (
            <div
              className={styles.confirmationSectionContent}
              style={{ backgroundColor: editedFields.jurisdiction !== extracted.jurisdiction ? "#fff4e6" : undefined }}
            >
              <Typography className={styles.confirmationSectionText}>
                {editedFields.jurisdiction || "UAE"}
              </Typography>
            </div>
          )}
        </div>

        {/* Key Objectives Section */}
        {(editedFields.objectives || extracted.objectives.length > 0) && (
          <div className={styles.confirmationSection}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography className={styles.confirmationSectionLabel}>
                Key Objectives
              </Typography>
              {editingField !== "objectives" && !isDisabled && (
                <IconButton size="small" onClick={() => handleEditClick("objectives")}>
                  <EditIcon sx={{ fontSize: 16, color: "#6b7280" }} />
                </IconButton>
              )}
            </Box>
            {editingField === "objectives" ? (
              <Box sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  size="small"
                  value={editedFields.objectives}
                  onChange={(e) => handleFieldChange("objectives", e.target.value)}
                  placeholder="One objective per line"
                  helperText="Enter one objective per line"
                />
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button size="small" variant="outlined" onClick={() => handleCancelEdit("objectives")}>Cancel</Button>
                  <Button size="small" variant="contained" onClick={handleSaveField} sx={{ backgroundColor: "#00a888" }}>Save</Button>
                </Stack>
              </Box>
            ) : (
              <div
                className={styles.confirmationSectionContent}
                style={{ backgroundColor: editedFields.objectives !== extracted.objectives.join("\n") ? "#fff4e6" : undefined }}
              >
                <ul className={styles.objectivesList}>
                  {editedFields.objectives.split("\n").filter((o) => o.trim()).map((obj, idx) => (
                    <li key={idx}>{obj.trim()}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer with Actions */}
      <div className={styles.confirmationFooter}>
        <div className={styles.confirmationHint}>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: "#6b7280" }} />
          <Typography className={styles.confirmationHintText}>
            Click on any field to edit. Save your changes to confirm and proceed to template selection.
          </Typography>
        </div>
        <div className={styles.confirmationActions}>
          <Button
            variant="contained"
            startIcon={isSubmitting ? undefined : <CheckCircleIcon sx={{ fontSize: 16 }} />}
            size="small"
            onClick={handleSubmitEdits}
            disabled={isDisabled || isSubmitting}
            sx={{
              textTransform: "none",
              backgroundColor: "#00a888",
              "&:hover": { backgroundColor: "#00947a" },
              "&:disabled": { backgroundColor: "#ccc" },
            }}
          >
            {isSubmitting ? "Saving..." : "Save & Confirm Template"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// INTAKE FORM VIEW - Interactive form for collecting MoU details
// ============================================================================
function IntakeFormView({
  data,
  prefill,
  onSend,
  isLocked = false,
  hasUserResponded = false,
}: {
  data: any;
  prefill?: any;
  onSend: (msg: string) => void;
  isLocked?: boolean;
  hasUserResponded?: boolean;
}): JSX.Element {
  // Disable all interactions if locked OR user has already responded
  const isDisabled = isLocked || hasUserResponded;
  const [firstParty] = useState("G42");
  const [counterpartyName, setCounterpartyName] = useState(prefill?.counterparty_name || "");
  const [counterpartyDescription, setCounterpartyDescription] = useState(prefill?.counterparty_description || "");
  const [counterpartyAddress, setCounterpartyAddress] = useState(prefill?.counterparty_address || "");
  const [projectName, setProjectName] = useState(prefill?.project_name || "");
  const [purposeAndScope, setPurposeAndScope] = useState(prefill?.collaboration_summary || "");
  const [additionalTerms, setAdditionalTerms] = useState(prefill?.additional_notes || "");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Joint Venture contribution fields (optional — Long-Form/JV MoUs only)
  const [g42InitialInvestment, setG42InitialInvestment] = useState(prefill?.g42_initial_investment || "");
  const [g42AdditionalInvestment, setG42AdditionalInvestment] = useState(prefill?.g42_additional_investment || "");
  const [g42NonMonetaryContribution, setG42NonMonetaryContribution] = useState(prefill?.g42_non_monetary_contribution || "");
  const [counterpartyInvestment, setCounterpartyInvestment] = useState(prefill?.counterparty_investment || "");
  const [counterpartyNonMonetaryContribution, setCounterpartyNonMonetaryContribution] = useState(prefill?.counterparty_non_monetary_contribution || "");
  const [counterpartySignatoryName, setCounterpartySignatoryName] = useState(prefill?.counterparty_signatory_name || "");
  const [counterpartySignatoryTitle, setCounterpartySignatoryTitle] = useState(prefill?.counterparty_signatory_title || "");

  // Jurisdiction handling: check if prefilled value is in predefined options
  // If not, set dropdown to "Other" and store the original value in otherJurisdiction
  const prefillJurisdiction = prefill?.jurisdiction || "";
  const isPrefillInOptions = JURISDICTION_OPTIONS.includes(prefillJurisdiction);
  const [jurisdiction, setJurisdiction] = useState(
    prefillJurisdiction
      ? (isPrefillInOptions ? prefillJurisdiction : "Other")
      : "UAE"
  );
  const [otherJurisdiction, setOtherJurisdiction] = useState(
    prefillJurisdiction && !isPrefillInOptions ? prefillJurisdiction : ""
  );

  // Confidence scores from document_prefill payload
  const confidenceScores: Record<string, number> = prefill?.confidence_scores || {};

  // Define thresholds for validation states
  const LOW_CONFIDENCE_THRESHOLD = 0.6;

  // Helper: Check if a required field is missing (empty value)
  const isFieldMissing = (value: string): boolean => !value.trim();

  // Helper: Check if a field has low confidence (score < 0.6)
  const isLowConfidence = (fieldKey: string): boolean => {
    const score = confidenceScores[fieldKey];
    return score !== undefined && score < LOW_CONFIDENCE_THRESHOLD;
  };

  // Helper: Get validation state for a field
  // Returns: 'error' for missing required, 'warning' for low confidence, 'valid' otherwise
  const getFieldValidationState = (
    fieldKey: string,
    value: string,
    isRequired: boolean = true
  ): "error" | "warning" | "valid" => {
    // Missing required field takes priority
    if (isRequired && isFieldMissing(value)) {
      return "error";
    }
    // Low confidence (only when there's a prefilled value with low score)
    if (value.trim() && isLowConfidence(fieldKey)) {
      return "warning";
    }
    return "valid";
  };

  // Helper: Get the appropriate CSS class for the input
  const getInputClassName = (fieldKey: string, value: string, isRequired: boolean = true): string => {
    const state = getFieldValidationState(fieldKey, value, isRequired);
    if (state === "warning") {
      return `${styles.intakeFormInput} ${styles.intakeFormInputWarning}`;
    }
    return styles.intakeFormInput;
  };

  // Helper: Get helper text for a field based on validation state
  const getFieldHelperText = (
    fieldKey: string,
    value: string,
    fieldLabel: string,
    isRequired: boolean = true
  ): string => {
    const state = getFieldValidationState(fieldKey, value, isRequired);
    if (state === "error") {
      return `This field is required — please provide the ${fieldLabel.toLowerCase()}.`;
    }
    if (state === "warning") {
      const score = confidenceScores[fieldKey];
      const percentage = score !== undefined ? Math.round(score * 100) : 0;
      return `⚠ Please verify — extracted with ${percentage}% confidence`;
    }
    return "";
  };

  // Notice to show when backend flags missing fields
  const missingFieldNotice = data?.question || "";

  // Response can be an array of strings or a string
  const responseRaw = data?.response;
  const responseText = typeof responseRaw === "string" ? responseRaw : "";

  // Form validation: also check otherJurisdiction when "Other" is selected
  const isJurisdictionValid = jurisdiction !== "Other" || otherJurisdiction.trim();
  const isFormValid = counterpartyName.trim() && counterpartyDescription.trim() && counterpartyAddress.trim() && projectName.trim() && purposeAndScope.trim() && isJurisdictionValid;

  // Get the actual jurisdiction value to submit (use otherJurisdiction when "Other" is selected)
  const getJurisdictionValue = (): string => {
    return jurisdiction === "Other" ? otherJurisdiction.trim() : jurisdiction;
  };

  const handleSubmit = () => {
    if (!isFormValid) return;

    setIsSubmitting(true);

    // Format the form data as a structured message matching backend expectations
    let formattedMessage = `1. Counterparty Name: ${counterpartyName.trim()}`;
    formattedMessage += `\n2. Counterparty Description: ${counterpartyDescription.trim()}`;
    formattedMessage += `\n3. Counterparty Address: ${counterpartyAddress.trim()}`;
    formattedMessage += `\n4. Project / Joint Venture / Collaboration: ${projectName.trim()}`;
    formattedMessage += `\n5. Purpose and Scope: ${purposeAndScope.trim()}`;
    formattedMessage += `\n6. Jurisdiction: ${getJurisdictionValue()}`;

    if (additionalTerms.trim()) {
      formattedMessage += `\n7. Additional Terms: ${additionalTerms.trim()}`;
    }

    // Joint Venture contribution fields (optional — only include what's filled in)
    const jvFields: Array<[string, string]> = [
      ["G42 Initial Investment", g42InitialInvestment.trim()],
      ["G42 Additional/Milestone Investment", g42AdditionalInvestment.trim()],
      ["G42 Non-Monetary Contribution", g42NonMonetaryContribution.trim()],
      ["Counterparty Investment", counterpartyInvestment.trim()],
      ["Counterparty Non-Monetary Contribution", counterpartyNonMonetaryContribution.trim()],
      ["Counterparty Signatory Name", counterpartySignatoryName.trim()],
      ["Counterparty Signatory Title", counterpartySignatoryTitle.trim()],
    ];
    const filledJvFields = jvFields.filter(([, value]) => value);
    if (filledJvFields.length > 0) {
      formattedMessage += `\n\nJoint Venture Contribution Details:`;
      filledJvFields.forEach(([label, value]) => {
        formattedMessage += `\n${label}: ${value}`;
      });
    }

    onSend(formattedMessage);
  };

  const handleJurisdictionChange = (event: SelectChangeEvent) => {
    const newValue = event.target.value;
    setJurisdiction(newValue);
    // Clear otherJurisdiction when switching away from "Other"
    if (newValue !== "Other") {
      setOtherJurisdiction("");
    }
  };

  return (
    <div className={styles.container} style={isDisabled ? { opacity: 0.7, pointerEvents: "none" } : undefined}>
      {/* Header */}
      <div className={styles.intakeFormHeader}>
        <div className={styles.intakeFormHeaderLeft}>
          <Typography className={styles.intakeFormEyebrow}>
            MoU INTAKE
          </Typography>
          <Typography className={styles.intakeFormTitle}>
            Memorandum of Understanding Details
          </Typography>
        </div>
        {isDisabled ? (
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
            label="Submitted"
            size="small"
            sx={{
              backgroundColor: "#d1fae5",
              color: "#065f46",
              fontWeight: 500,
              "& .MuiChip-icon": { color: "#059669" },
            }}
          />
        ) : (
          <div className={styles.intakeFormBadge}>
            <span className={styles.intakeFormBadgeText}>Step 1 of 3</span>
          </div>
        )}
      </div>

      {/* Form Fields */}
      <div className={styles.intakeFormBody}>
        {/* Response message */}
        {/* Missing-field notice from backend */}
        {missingFieldNotice && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: "8px",
              backgroundColor: "#fff8f0",
              border: "1px solid #f59e0b",
              display: "flex",
              gap: 1,
              alignItems: "flex-start",
            }}
          >
            <InfoOutlinedIcon sx={{ color: "#f59e0b", fontSize: 18, mt: "2px", flexShrink: 0 }} />
            <Typography sx={{ fontSize: "13px", color: "#92400e", lineHeight: 1.5 }}>
              <MarkdownRenderer content={missingFieldNotice} />
            </Typography>
          </Box>
        )}
        {/* Generic response text (non-prefill path) */}
        {responseText && (
          <div className={styles.responseMessage}>
            <MarkdownRenderer content={responseText} />
          </div>
        )}
        {/* First Party (Pre-filled, Read-only) */}
        <div className={styles.firstPartyBanner}>
          <div className={styles.firstPartyHeader}>
            <BusinessIcon sx={{ fontSize: 20, color: "#00a888" }} />
            <Typography className={styles.firstPartyTitle}>
              First Party (Pre-filled)
            </Typography>
          </div>
          <div className={styles.firstPartyContent}>
            <Typography className={styles.firstPartyName}>{firstParty}</Typography>
            <Typography className={styles.firstPartyLegalName}>
              {G42_LEGAL_NAME}
            </Typography>
            <Typography className={styles.firstPartyDescription}>
              {G42_DESCRIPTION}
            </Typography>
          </div>
        </div>

        {/* Counterparty Name */}
        <div className={styles.intakeFormField}>
          <Typography className={styles.intakeFormLabel}>
            Counterparty Name <span className={styles.required}>*</span>
          </Typography>
          <Typography className={styles.intakeFormHint}>
            Full legal name of the party entering into this MoU with G42
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g., ABC Technologies LLC, Ministry of Digital Economy"
            value={counterpartyName}
            onChange={(e) => setCounterpartyName(e.target.value)}
            size="small"
            variant="outlined"
            className={getInputClassName("counterparty_name", counterpartyName)}
            error={getFieldValidationState("counterparty_name", counterpartyName) === "error"}
            helperText={getFieldHelperText("counterparty_name", counterpartyName, "counterparty name")}
          />
        </div>

        {/* Counterparty Description */}
        <div className={styles.intakeFormField}>
          <Typography className={styles.intakeFormLabel}>
            Counterparty Description <span className={styles.required}>*</span>
          </Typography>
          <Typography className={styles.intakeFormHint}>
            Brief description of the counterparty's business or nature
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g., a technology company specializing in cloud solutions and AI services"
            value={counterpartyDescription}
            onChange={(e) => setCounterpartyDescription(e.target.value)}
            size="small"
            variant="outlined"
            multiline
            rows={2}
            className={getInputClassName("counterparty_description", counterpartyDescription)}
            error={getFieldValidationState("counterparty_description", counterpartyDescription) === "error"}
            helperText={getFieldHelperText("counterparty_description", counterpartyDescription, "counterparty description")}
          />
        </div>

        {/* Counterparty Address */}
        <div className={styles.intakeFormField}>
          <Typography className={styles.intakeFormLabel}>
            Counterparty Address <span className={styles.required}>*</span>
          </Typography>
          <Typography className={styles.intakeFormHint}>
            Registered address or city of the counterparty
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g., Dubai, UAE or full registered address"
            value={counterpartyAddress}
            onChange={(e) => setCounterpartyAddress(e.target.value)}
            size="small"
            variant="outlined"
            className={getInputClassName("counterparty_address", counterpartyAddress)}
            error={getFieldValidationState("counterparty_address", counterpartyAddress) === "error"}
            helperText={getFieldHelperText("counterparty_address", counterpartyAddress, "counterparty address")}
            autoFocus={getFieldValidationState("counterparty_address", counterpartyAddress) === "error"}
          />
        </div>

        {/* Project / Joint Venture / Collaboration */}
        <div className={styles.intakeFormField}>
          <Typography className={styles.intakeFormLabel}>
            Project / Joint Venture / Collaboration <span className={styles.required}>*</span>
          </Typography>
          <Typography className={styles.intakeFormHint}>
            Name or title of the initiative
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g., AI Healthcare Platform, Smart City Infrastructure Partnership"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            size="small"
            variant="outlined"
            className={getInputClassName("project_name", projectName)}
            error={getFieldValidationState("project_name", projectName) === "error"}
            helperText={getFieldHelperText("project_name", projectName, "project name")}
          />
        </div>

        {/* Purpose and Scope */}
        <div className={styles.intakeFormField}>
          <Typography className={styles.intakeFormLabel}>
            Purpose and Scope <span className={styles.required}>*</span>
          </Typography>
          <Typography className={styles.intakeFormHint}>
            What G42 and the counterparty intend to achieve together
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g., Joint development of AI-powered diagnostic tools for healthcare facilities..."
            value={purposeAndScope}
            onChange={(e) => setPurposeAndScope(e.target.value)}
            size="small"
            variant="outlined"
            multiline
            rows={3}
            className={getInputClassName("collaboration_summary", purposeAndScope)}
            error={getFieldValidationState("collaboration_summary", purposeAndScope) === "error"}
            helperText={getFieldHelperText("collaboration_summary", purposeAndScope, "purpose and scope")}
          />
        </div>

        {/* Jurisdiction */}
        <div className={styles.intakeFormFieldRow}>
          <div className={styles.intakeFormFieldHalf}>
            <Typography className={styles.intakeFormLabel}>
              Jurisdiction
            </Typography>
            <Typography className={styles.intakeFormHint}>
              Governing law (default: UAE)
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={jurisdiction}
                onChange={handleJurisdictionChange}
                className={styles.intakeFormSelect}
              >
                {JURISDICTION_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Show text input when "Other" is selected */}
            {jurisdiction === "Other" && (
              <TextField
                fullWidth
                placeholder="Enter jurisdiction (e.g., Kingdom of Saudi Arabia, Republic of France)"
                value={otherJurisdiction}
                onChange={(e) => setOtherJurisdiction(e.target.value)}
                size="small"
                variant="outlined"
                className={styles.intakeFormInput}
                error={!otherJurisdiction.trim()}
                helperText={!otherJurisdiction.trim() ? "Please specify the jurisdiction" : ""}
                sx={{ mt: 1.5 }}
              />
            )}
          </div>
        </div>

        {/* Additional Terms */}
        <div className={styles.intakeFormField}>
          <Typography className={styles.intakeFormLabel}>
            Additional Terms
            <span className={styles.optional}> (optional)</span>
          </Typography>
          <Typography className={styles.intakeFormHint}>
            Duration, special requirements, or specific provisions
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g., 12-month term, Include IP licensing provisions, Add data protection clause..."
            value={additionalTerms}
            onChange={(e) => setAdditionalTerms(e.target.value)}
            size="small"
            variant="outlined"
            multiline
            rows={2}
            className={getInputClassName("additional_notes", additionalTerms, false)}
            helperText={getFieldHelperText("additional_notes", additionalTerms, "additional terms", false)}
          />
        </div>

        {/* Joint Venture Contribution Details (optional — Long-Form/JV MoUs only) */}
        <Box sx={{ mt: 3, mb: 1, pt: 2, borderTop: "1px solid #e5e7eb" }}>
          <Typography className={styles.intakeFormLabel} sx={{ fontWeight: 700 }}>
            Joint Venture Contribution Details
            <span className={styles.optional}> (optional — Long-Form/JV MoUs only)</span>
          </Typography>
        </Box>

        <div className={styles.intakeFormField}>
          <Typography className={styles.intakeFormLabel}>
            G42 Initial Investment
            <span className={styles.optional}> (optional)</span>
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g., AED 5,000,000"
            value={g42InitialInvestment}
            onChange={(e) => setG42InitialInvestment(e.target.value)}
            size="small"
            variant="outlined"
            className={styles.intakeFormInput}
          />
        </div>

        <div className={styles.intakeFormField}>
          <Typography className={styles.intakeFormLabel}>
            G42 Additional/Milestone Investment
            <span className={styles.optional}> (optional)</span>
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g., AED 2,000,000 upon achieving Milestone 1"
            value={g42AdditionalInvestment}
            onChange={(e) => setG42AdditionalInvestment(e.target.value)}
            size="small"
            variant="outlined"
            className={styles.intakeFormInput}
          />
        </div>

        <div className={styles.intakeFormField}>
          <Typography className={styles.intakeFormLabel}>
            G42 Non-Monetary Contribution
            <span className={styles.optional}> (optional)</span>
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g., technical expertise, IP licensing, office space"
            value={g42NonMonetaryContribution}
            onChange={(e) => setG42NonMonetaryContribution(e.target.value)}
            size="small"
            variant="outlined"
            multiline
            rows={2}
            className={styles.intakeFormInput}
          />
        </div>

        <div className={styles.intakeFormField}>
          <Typography className={styles.intakeFormLabel}>
            Counterparty Investment
            <span className={styles.optional}> (optional)</span>
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g., AED 3,000,000"
            value={counterpartyInvestment}
            onChange={(e) => setCounterpartyInvestment(e.target.value)}
            size="small"
            variant="outlined"
            className={styles.intakeFormInput}
          />
        </div>

        <div className={styles.intakeFormField}>
          <Typography className={styles.intakeFormLabel}>
            Counterparty Non-Monetary Contribution
            <span className={styles.optional}> (optional)</span>
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g., market access, facilities, local expertise"
            value={counterpartyNonMonetaryContribution}
            onChange={(e) => setCounterpartyNonMonetaryContribution(e.target.value)}
            size="small"
            variant="outlined"
            multiline
            rows={2}
            className={styles.intakeFormInput}
          />
        </div>

        <div className={styles.intakeFormFieldRow}>
          <div className={styles.intakeFormFieldHalf}>
            <Typography className={styles.intakeFormLabel}>
              Counterparty Signatory Name
              <span className={styles.optional}> (optional)</span>
            </Typography>
            <TextField
              fullWidth
              placeholder="e.g., Jane Doe"
              value={counterpartySignatoryName}
              onChange={(e) => setCounterpartySignatoryName(e.target.value)}
              size="small"
              variant="outlined"
              className={styles.intakeFormInput}
            />
          </div>
          <div className={styles.intakeFormFieldHalf}>
            <Typography className={styles.intakeFormLabel}>
              Counterparty Signatory Title
              <span className={styles.optional}> (optional)</span>
            </Typography>
            <TextField
              fullWidth
              placeholder="e.g., Chief Executive Officer"
              value={counterpartySignatoryTitle}
              onChange={(e) => setCounterpartySignatoryTitle(e.target.value)}
              size="small"
              variant="outlined"
              className={styles.intakeFormInput}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className={styles.intakeFormFooter}>
        <Typography className={styles.intakeFormNote}>
          <span className={styles.required}>*</span> Required fields
        </Typography>
        <Button
          variant="contained"
          endIcon={<SendIcon />}
          onClick={handleSubmit}
          disabled={!isFormValid || isSubmitting}
          sx={{
            textTransform: "none",
            backgroundColor: "#00a888",
            "&:hover": { backgroundColor: "#00947a" },
            "&:disabled": { backgroundColor: "#ccc" },
          }}
        >
          {isSubmitting ? "Submitting..." : "Submit Details"}
        </Button>
      </div>
    </div>
  );
}

// Helper to parse recommendation from response text
function parseRecommendation(responseText: string): {
  recommendedTemplate: "short" | "long" | null;
  confidence: number | null;
  reason: string;
} {
  const result = {
    recommendedTemplate: null as "short" | "long" | null,
    confidence: null as number | null,
    reason: "",
  };

  // Parse recommendation: **Recommendation:** Short-Form MoU (96% confidence)
  const recommendationMatch = responseText.match(/\*\*Recommendation:\*\*\s*(Short-Form|Long-Form)\s*MoU\s*\((\d+)%\s*confidence\)/i);
  if (recommendationMatch) {
    result.recommendedTemplate = recommendationMatch[1].toLowerCase().includes("short") ? "short" : "long";
    result.confidence = parseInt(recommendationMatch[2], 10);
  }

  // Parse reason: **Reason:** ...
  const reasonMatch = responseText.match(/\*\*Reason:\*\*\s*([\s\S]*?)(?=\n\n|$)/);
  if (reasonMatch) {
    result.reason = reasonMatch[1].trim();
  }

  return result;
}

// ============================================================================
// TEMPLATE SELECTION VIEW - Toggle buttons for Short-Form vs Long-Form
// ============================================================================
function TemplateSelectionView({
  data,
  responseText,
  onSend,
  isLocked = false,
  hasUserResponded = false,
}: {
  data: any;
  responseText: string;
  onSend: (msg: string) => void;
  isLocked?: boolean;
  hasUserResponded?: boolean;
}): JSX.Element {
  // Disable all interactions if locked OR user has already responded
  const isDisabled = isLocked || hasUserResponded;

  // Parse recommendation from response
  const recommendation = parseRecommendation(responseText);

  // Pre-select recommended template if available
  const [selectedTemplate, setSelectedTemplate] = useState<"short" | "long" | null>(
    recommendation.recommendedTemplate
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showReason, setShowReason] = useState(false);

  const handleSelectTemplate = (template: "short" | "long") => {
    if (isDisabled) return;
    setSelectedTemplate(template);
  };

  const handleConfirmSelection = () => {
    if (!selectedTemplate || isDisabled) return;
    setIsSubmitting(true);
    onSend(selectedTemplate);
  };

  const isRecommended = (template: "short" | "long") => recommendation.recommendedTemplate === template;

  return (
    <div className={styles.container} style={isDisabled ? { opacity: 0.7, pointerEvents: "none" } : undefined}>
      {/* Header */}
      <div className={styles.templateSelectionHeader}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", width: "100%" }}>
          <div>
            <Typography className={styles.templateSelectionEyebrow}>
              TEMPLATE SELECTION
            </Typography>
            <Typography className={styles.templateSelectionTitle}>
              Choose Your MoU Template
            </Typography>
            <Typography className={styles.templateSelectionSubtitle}>
              Select the template type that best fits your collaboration needs
            </Typography>
          </div>
          {isDisabled && (
            <Chip
              icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
              label="Submitted"
              size="small"
              sx={{
                backgroundColor: "#d1fae5",
                color: "#065f46",
                fontWeight: 500,
                "& .MuiChip-icon": { color: "#059669" },
              }}
            />
          )}
        </Box>
      </div>

      {/* Template Options */}
      <div className={styles.templateOptions}>
        {/* Short-Form Option */}
        <div
          className={`${styles.templateOption} ${selectedTemplate === "short" ? styles.templateOptionSelected : ""} ${isRecommended("short") && selectedTemplate !== "long" ? styles.templateOptionRecommended : ""}`}
          onClick={() => handleSelectTemplate("short")}
        >
          <div className={styles.templateOptionHeader}>
            <div className={styles.templateOptionRadio}>
              {selectedTemplate === "short" ? (
                <CheckCircleIcon sx={{ fontSize: 24, color: "#00a888" }} />
              ) : (
                <div className={styles.templateOptionRadioEmpty} />
              )}
            </div>
            <div className={styles.templateOptionTitleArea}>
              <Typography className={styles.templateOptionTitle}>
                Short-Form MoU
              </Typography>
              <Chip
                label="2-3 pages"
                size="small"
                sx={{
                  fontSize: "11px",
                  height: "20px",
                  backgroundColor: "#f0fbf9",
                  color: "#004141",
                  border: "1px solid #b2e5de",
                }}
              />
              {isRecommended("short") && selectedTemplate !== "long" && recommendation.confidence && (
                <Chip
                  label={`Recommended (${recommendation.confidence}%)`}
                  size="small"
                  sx={{
                    fontSize: "11px",
                    height: "20px",
                    backgroundColor: "#00a888",
                    color: "#fff",
                    fontWeight: 600,
                    ml: 0.5,
                  }}
                />
              )}
            </div>
          </div>
          <Typography className={styles.templateOptionDescription}>
            A concise, high-level agreement suitable for:
          </Typography>
          <ul className={styles.templateOptionList}>
            <li>Exploratory collaborations</li>
            <li>Initial partnership discussions</li>
            <li>Simple bilateral agreements</li>
            <li>Non-binding expressions of intent</li>
          </ul>
          <Typography className={styles.templateOptionNote}>
            Best for early-stage discussions or straightforward partnerships
          </Typography>
        </div>

        {/* Long-Form Option */}
        <div
          className={`${styles.templateOption} ${selectedTemplate === "long" ? styles.templateOptionSelected : ""} ${isRecommended("long") && selectedTemplate !== "short" ? styles.templateOptionRecommended : ""}`}
          onClick={() => handleSelectTemplate("long")}
        >
          <div className={styles.templateOptionHeader}>
            <div className={styles.templateOptionRadio}>
              {selectedTemplate === "long" ? (
                <CheckCircleIcon sx={{ fontSize: 24, color: "#00a888" }} />
              ) : (
                <div className={styles.templateOptionRadioEmpty} />
              )}
            </div>
            <div className={styles.templateOptionTitleArea}>
              <Typography className={styles.templateOptionTitle}>
                Long-Form MoU
              </Typography>
              <Chip
                label="5-8 pages"
                size="small"
                sx={{
                  fontSize: "11px",
                  height: "20px",
                  backgroundColor: "#f0fbf9",
                  color: "#004141",
                  border: "1px solid #b2e5de",
                }}
              />
              {isRecommended("long") && selectedTemplate !== "short" && recommendation.confidence && (
                <Chip
                  label={`Recommended (${recommendation.confidence}%)`}
                  size="small"
                  sx={{
                    fontSize: "11px",
                    height: "20px",
                    backgroundColor: "#00a888",
                    color: "#fff",
                    fontWeight: 600,
                    ml: 0.5,
                  }}
                />
              )}
            </div>
          </div>
          <Typography className={styles.templateOptionDescription}>
            A comprehensive agreement suitable for:
          </Typography>
          <ul className={styles.templateOptionList}>
            <li>Multi-phase projects</li>
            <li>Complex collaborations with multiple stakeholders</li>
            <li>Agreements requiring detailed governance</li>
            <li>Partnerships with IP, data, or financial considerations</li>
          </ul>
          <Typography className={styles.templateOptionNote}>
            Best for detailed partnerships requiring comprehensive terms
          </Typography>
        </div>
      </div>

      {/* Recommendation Reason Section */}
      {recommendation.reason && (
        <div className={styles.recommendationReason}>
          <div
            className={styles.recommendationReasonHeader}
            onClick={() => setShowReason(!showReason)}
          >
            <div className={styles.recommendationReasonHeaderLeft}>
              <InfoOutlinedIcon sx={{ fontSize: 18, color: "#00a888" }} />
              <Typography className={styles.recommendationReasonTitle}>
                Why {recommendation.recommendedTemplate === "short" ? "Short-Form" : "Long-Form"} MoU is recommended
              </Typography>
            </div>
            <IconButton size="small">
              {showReason ? (
                <ExpandLessIcon sx={{ color: "#004141" }} />
              ) : (
                <ExpandMoreIcon sx={{ color: "#004141" }} />
              )}
            </IconButton>
          </div>
          {showReason && (
            <div className={styles.recommendationReasonContent}>
              <Typography className={styles.recommendationReasonText}>
                {recommendation.reason}
              </Typography>
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className={styles.templateSelectionFooter}>
        <div className={styles.templateSelectionHint}>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: "#6b7280" }} />
          <Typography className={styles.templateSelectionHintText}>
            You can always request changes after reviewing the draft
          </Typography>
        </div>
        <Button
          variant="contained"
          onClick={handleConfirmSelection}
          disabled={!selectedTemplate || isSubmitting}
          startIcon={<CheckCircleIcon sx={{ fontSize: 18 }} />}
          sx={{
            textTransform: "none",
            backgroundColor: "#00a888",
            px: 4,
            "&:hover": { backgroundColor: "#00947a" },
            "&:disabled": { backgroundColor: "#ccc" },
          }}
        >
          {isSubmitting
            ? "Submitting..."
            : selectedTemplate
              ? `Continue with ${selectedTemplate === "short" ? "Short-Form" : "Long-Form"}`
              : "Select a Template"}
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// INTAKE PROMPT VIEW - Step 1: Agent asks for 5 fields (non-form fallback)
// ============================================================================
function IntakePromptView({
  data,
  message,
}: {
  data: any;
  message: string;
}): JSX.Element {
  return (
    <div className={styles.container}>
      <div className={styles.sectionHeader}>
        <DescriptionIcon sx={{ color: "#00a888", fontSize: 20 }} />
        <Typography className={styles.sectionTitle}>
          MoU Intake Request
        </Typography>
      </div>

      <div className={styles.questionBox}>
        <div className={styles.questionIcon}>
          <InfoOutlinedIcon sx={{ color: "#00a888", fontSize: 18 }} />
        </div>
        <div className={styles.questionContent}>
          {message ? (
            <MarkdownRenderer content={message} />
          ) : (
            <Typography component="div">
              <p>Please provide the following details to create your Memorandum of Understanding:</p>
              <br />
              1. <strong>Counterparty</strong> - Full legal name of the party entering into this MoU with G42
              <br />
              2. <strong>Project / Joint Venture / Collaboration</strong> - Name or title of the initiative
              <br />
              3. <strong>Purpose and Scope</strong> - What G42 and the counterparty intend to achieve together
              <br />
              4. <strong>Counterparty Address</strong> (optional) - Registered address or city
              <br />
              5. <strong>Jurisdiction</strong> - Governing law (default: UAE)
              <br />
              6. <strong>Additional Terms</strong> (optional) - Duration, special requirements, or specific provisions
            </Typography>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// CONVERSATION VIEW - HITL with question field (non-intake questions)
// ============================================================================
function extractDocxUrl(text: string): string | null {
  const match = text.match(/\[Download DOCX\]\((https?[^)]+)\)/i);
  return match?.[1] || null;
}

function stripPdfLines(text: string): string {
  return text
    .split("\n")
    .filter(
      (line) =>
        !/\[download\s+pdf\]/i.test(line) &&
        !/pdf\s+unavailable/i.test(line)
    )
    .join("\n");
}

function ConversationView({
  data,
  onSend,
  isLocked = false,
  hasUserResponded = false,
}: {
  data: any;
  onSend: (msg: string) => void;
  isLocked?: boolean;
  hasUserResponded?: boolean;
}): JSX.Element {
  // Disable all interactions if locked OR user has already responded
  const isDisabled = isLocked || hasUserResponded;
  const question = data?.question || "";
  // Response can be an array of strings or a string
  const responseRaw = data?.response;
  const rawResponseText = Array.isArray(responseRaw)
    ? responseRaw.join("\n\n")
    : responseRaw || "";

  // Strip PDF download lines (removed from backend) and extract DOCX URL for preview
  const docxUrl = extractDocxUrl(rawResponseText);
  const responseText = stripPdfLines(rawResponseText);

  // Detect quick actions from question text
  const actions = getConversationActions(question);

  // Detect if this is an entity verification step (mou_entity_agent)
  const isEntityVerification = data?.agent_id === "mou_entity_agent";

  const handlePreviewDocx = () => {
    if (docxUrl) {
      // Add wdAllowInteractivity=False to ensure full document rendering
      const viewerUrl = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(docxUrl)}&action=embedview&wdAllowInteractivity=False`;
      window.open(viewerUrl, "_blank");
    }
  };

  return (
    <div className={styles.conversationContainer} style={isDisabled ? { opacity: 0.7, pointerEvents: "none" } : undefined}>
      {/* Locked/Completed Badge */}
      {isLocked && (
        <Chip
          icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
          label="Completed"
          size="small"
          sx={{
            mb: 2,
            backgroundColor: "#d1fae5",
            color: "#065f46",
            fontWeight: 500,
            "& .MuiChip-icon": { color: "#059669" },
          }}
        />
      )}
      {/* Entity Verification Badge */}
      {isEntityVerification && !isLocked && (
        <Chip
          icon={<SearchIcon sx={{ fontSize: 16 }} />}
          label="Verifying entity..."
          size="small"
          sx={{
            mb: 2,
            backgroundColor: "#fff8e1",
            color: "#f57c00",
            border: "1px solid #ffcc80",
            fontWeight: 500,
            "& .MuiChip-icon": {
              color: "#f57c00",
            },
          }}
        />
      )}

      {responseText && (
        <div className={styles.responseText}>
          <MarkdownRenderer content={responseText} />
        </div>
      )}

      {/* DOCX preview button — shown when a download link is present in the response */}
      {docxUrl && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
          onClick={handlePreviewDocx}
          sx={{
            mt: 1,
            mb: 1,
            textTransform: "none",
            borderColor: "#b2e5de",
            color: "#004141",
            "&:hover": { borderColor: "#00a888", backgroundColor: "#f0fbf9" },
          }}
        >
          Preview Document
        </Button>
      )}

      {question && (
        <div
          className={styles.questionBox}
          style={
            isEntityVerification
              ? {
                  backgroundColor: "#fffbf0",
                  borderColor: "#ffcc80",
                }
              : undefined
          }
        >
          <div className={styles.questionIcon}>
            {isEntityVerification ? (
              <SearchIcon sx={{ color: "#f57c00", fontSize: 18 }} />
            ) : (
              <InfoOutlinedIcon sx={{ color: "#00a888", fontSize: 18 }} />
            )}
          </div>
          <div className={styles.questionContent}>
            <MarkdownRenderer content={question} />
          </div>
        </div>
      )}

      {actions.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap" }}>
          {actions.map((action, idx) => (
            <Button
              key={idx}
              variant={action.variant || "outlined"}
              size="small"
              onClick={() => onSend(action.message)}
              disabled={isDisabled}
              sx={{
                textTransform: "none",
                borderColor: isEntityVerification ? "#f57c00" : "#00a888",
                color:
                  action.variant === "contained"
                    ? "#fff"
                    : isEntityVerification
                      ? "#e65100"
                      : "#004141",
                backgroundColor:
                  action.variant === "contained"
                    ? isEntityVerification
                      ? "#f57c00"
                      : "#00a888"
                    : "transparent",
                "&:hover": {
                  borderColor: isEntityVerification ? "#e65100" : "#00947a",
                  backgroundColor:
                    action.variant === "contained"
                      ? isEntityVerification
                        ? "#e65100"
                        : "#00947a"
                      : isEntityVerification
                        ? "#fff8e1"
                        : "#f0fbf9",
                },
                "&:disabled": { backgroundColor: "#ccc", borderColor: "#ccc" },
              }}
            >
              {action.label}
            </Button>
          ))}
        </Stack>
      )}
    </div>
  );
}

// ============================================================================
// INTAKE SUMMARY VIEW - mou_intake_agent: shows extracted counterparty/project data
// ============================================================================
function IntakeSummaryView({ result, isLocked = false }: { result: any; isLocked?: boolean }): JSX.Element {
  const counterpartyName = result?.counterparty_name || "";
  const counterpartyDescription = result?.counterparty_description || "";
  const counterpartyAddress = result?.counterparty_address || "";
  const counterpartyType = result?.counterparty_type || "";
  const projectName = result?.project_name || "";
  const collaborationSummary = result?.collaboration_summary || "";
  const jurisdiction = result?.jurisdiction || "";
  const additionalNotes = result?.additional_notes || "";
  const keyObjectives: string[] = result?.key_objectives || [];
  const confidenceScores: Record<string, number> = result?.confidence_scores || {};

  const formatCounterpartyType = (type: string) => {
    return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <div className={styles.container} style={isLocked ? { opacity: 0.7 } : undefined}>
      <div className={styles.sectionHeader}>
        <InfoOutlinedIcon sx={{ color: "#004141", fontSize: 20 }} />
        <Typography className={styles.sectionTitle}>
          Intake Summary
        </Typography>
        {isLocked ? (
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
            label="Completed"
            size="small"
            sx={{
              backgroundColor: "#d1fae5",
              color: "#065f46",
              fontWeight: 500,
              ml: 1,
              "& .MuiChip-icon": { color: "#059669" },
            }}
          />
        ) : (
          <Chip
            label="Extracted"
            size="small"
            sx={{
              backgroundColor: "#f0fbf9",
              color: "#004141",
              border: "1px solid #b2e5de",
              fontWeight: 600,
              ml: 1,
            }}
          />
        )}
      </div>

      <div className={styles.detailsGrid}>
        {counterpartyName && (
          <div className={styles.detailItem}>
            <Typography className={styles.detailLabel}>Counterparty Name</Typography>
            <Typography className={styles.detailValue}>{counterpartyName}</Typography>
          </div>
        )}
        {counterpartyType && (
          <div className={styles.detailItem}>
            <Typography className={styles.detailLabel}>Counterparty Type</Typography>
            <Typography className={styles.detailValue}>{formatCounterpartyType(counterpartyType)}</Typography>
          </div>
        )}
        {projectName && (
          <div className={styles.detailItem}>
            <Typography className={styles.detailLabel}>Project Name</Typography>
            <Typography className={styles.detailValue}>{projectName}</Typography>
          </div>
        )}
        {jurisdiction && (
          <div className={styles.detailItem}>
            <Typography className={styles.detailLabel}>Jurisdiction</Typography>
            <Typography className={styles.detailValue}>{jurisdiction}</Typography>
          </div>
        )}
      </div>

      {counterpartyAddress && (
        <div className={styles.section}>
          <Typography className={styles.sectionLabel}>Counterparty Address</Typography>
          <div className={styles.summaryBox}>
            <Typography className={styles.summaryText}>{counterpartyAddress}</Typography>
          </div>
        </div>
      )}

      {counterpartyDescription && (
        <div className={styles.section}>
          <Typography className={styles.sectionLabel}>Counterparty Description</Typography>
          <div className={styles.summaryBox}>
            <Typography className={styles.summaryText}>{counterpartyDescription}</Typography>
          </div>
        </div>
      )}

      {collaborationSummary && (
        <div className={styles.section}>
          <Typography className={styles.sectionLabel}>Collaboration Summary</Typography>
          <div className={styles.summaryBox}>
            <Typography className={styles.summaryText}>{collaborationSummary}</Typography>
          </div>
        </div>
      )}

      {keyObjectives.length > 0 && (
        <div className={styles.section}>
          <Typography className={styles.sectionLabel}>Key Objectives</Typography>
          <Stack spacing={1}>
            {keyObjectives.map((obj, idx) => (
              <div key={idx} className={styles.objectiveItem}>
                <span className={styles.bulletDot} />
                <Typography className={styles.objectiveText}>{obj}</Typography>
              </div>
            ))}
          </Stack>
        </div>
      )}

      {additionalNotes && (
        <div className={styles.section}>
          <Typography className={styles.sectionLabel}>Additional Notes</Typography>
          <div className={styles.summaryBox}>
            <Typography className={styles.summaryText}>{additionalNotes}</Typography>
          </div>
        </div>
      )}

      {Object.keys(confidenceScores).length > 0 && (
        <div className={styles.section}>
          <Typography className={styles.sectionLabel}>Confidence Scores</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {Object.entries(confidenceScores).map(([field, score]) => (
              <Chip
                key={field}
                label={`${field.replace(/_/g, " ")}: ${Math.round(Number(score) * 100)}%`}
                size="small"
                sx={{
                  backgroundColor: "#f0fbf9",
                  color: "#004141",
                  border: "1px solid #b2e5de",
                  fontWeight: 500,
                  fontSize: "11px",
                  mb: 1,
                }}
              />
            ))}
          </Stack>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// CONFIRMATION VIEW - Step 2/3: Display extracted data for confirmation
// ============================================================================
function ConfirmationView({
  data,
  result,
  onSend,
  isLocked = false,
  hasUserResponded = false,
}: {
  data: any;
  result: any;
  onSend: (msg: string) => void;
  isLocked?: boolean;
  hasUserResponded?: boolean;
}): JSX.Element {
  // Disable all interactions if locked OR user has already responded
  const isDisabled = isLocked || hasUserResponded;
  const [copied, setCopied] = useState(false);

  // Extract fields from various possible locations
  const counterparty = data?.counterparty || result?.counterparty || "";
  const projectName = data?.project_name || result?.project_name || "";
  const collaborationSummary =
    data?.collaboration_summary || result?.collaboration_summary || "";
  const jurisdiction = data?.jurisdiction || result?.jurisdiction || "UAE";
  const parties = data?.parties || result?.parties || ["G42", counterparty];
  const keyObjectives =
    data?.key_objectives || result?.key_objectives || data?.objectives || [];
  const templateType =
    data?.template_type || result?.template_type || "Short-Form MoU";
  const confidence = data?.confidence || result?.confidence || null;
  const reasoning = data?.reasoning || result?.reasoning || "";
  const rawMessage = data?.message || result?.message || "";

  // Check for correction count (Phase 6 - Draft Corrections)
  const correctionCount =
    data?.correction_count || result?.correction_count || 0;
  const hasCorrectionApplied = correctionCount > 0;

  // Parse message to extract playbook for citation block
  const { mainContent: message, playbook, sources, sourcesNotConfigured } =
    parsePlaybookAndSources(rawMessage);

  const handleCopy = () => {
    const text = `Counterparty: ${counterparty}\nProject: ${projectName}\nCollaboration: ${collaborationSummary}\nJurisdiction: ${jurisdiction}\nTemplate: ${templateType}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={styles.container} style={isDisabled ? { opacity: 0.7, pointerEvents: "none" } : undefined}>
      {/* Locked/Completed Indicator */}
      {isDisabled && (
        <Chip
          icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
          label="Completed"
          size="small"
          sx={{
            mb: 2,
            backgroundColor: "#d1fae5",
            color: "#065f46",
            fontWeight: 500,
            "& .MuiChip-icon": { color: "#059669" },
          }}
        />
      )}
      {/* Correction Applied Indicator */}
      {hasCorrectionApplied && !isDisabled && (
        <Chip
          icon={<AutorenewIcon sx={{ fontSize: 16 }} />}
          label={`Correction applied${correctionCount > 1 ? ` (${correctionCount})` : ""} — please re-confirm`}
          size="small"
          sx={{
            mb: 2,
            backgroundColor: "#e3f2fd",
            color: "#1565c0",
            border: "1px solid #90caf9",
            fontWeight: 500,
            "& .MuiChip-icon": {
              color: "#1565c0",
            },
          }}
        />
      )}

      <div className={styles.sectionHeader}>
        <CheckCircleIcon sx={{ color: "#00a888", fontSize: 20 }} />
        <Typography className={styles.sectionTitle}>
          MoU Confirmation
        </Typography>
        <Tooltip title={copied ? "Copied!" : "Copy details"}>
          <IconButton size="small" onClick={handleCopy}>
            <ContentCopyIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </div>

      {message && (
        <div className={styles.summaryBox}>
          <MarkdownRenderer content={message} />
        </div>
      )}

      {/* Citation block for playbook */}
      <CitationBlock
        playbook={playbook}
        sources={sources}
        sourcesNotConfigured={sourcesNotConfigured}
      />

      <div className={styles.detailsGrid}>
        {counterparty && (
          <div className={styles.detailItem}>
            <Typography className={styles.detailLabel}>Counterparty</Typography>
            <Typography className={styles.detailValue}>
              {counterparty}
            </Typography>
          </div>
        )}

        {projectName && (
          <div className={styles.detailItem}>
            <Typography className={styles.detailLabel}>Project</Typography>
            <Typography className={styles.detailValue}>{projectName}</Typography>
          </div>
        )}

        {jurisdiction && (
          <div className={styles.detailItem}>
            <Typography className={styles.detailLabel}>Jurisdiction</Typography>
            <Typography className={styles.detailValue}>{jurisdiction}</Typography>
          </div>
        )}

        {templateType && (
          <div className={styles.detailItem}>
            <Typography className={styles.detailLabel}>Template</Typography>
            <Typography className={styles.detailValue}>
              {templateType}
              {confidence && (
                <Chip
                  label={`${Math.round(confidence * 100)}% confidence`}
                  size="small"
                  sx={{
                    ml: 1,
                    fontSize: "11px",
                    height: "20px",
                    backgroundColor: "#f0fbf9",
                    color: "#004141",
                    border: "1px solid #b2e5de",
                  }}
                />
              )}
            </Typography>
          </div>
        )}
      </div>

      {collaborationSummary && (
        <div className={styles.section}>
          <Typography className={styles.sectionLabel}>
            Collaboration Summary
          </Typography>
          <div className={styles.summaryBox}>
            <Typography className={styles.summaryText}>
              {collaborationSummary}
            </Typography>
          </div>
        </div>
      )}

      {parties && parties.length > 0 && (
        <div className={styles.section}>
          <Typography className={styles.sectionLabel}>Parties</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {parties.map((party: string, idx: number) => (
              <Chip
                key={idx}
                label={party}
                size="small"
                sx={{
                  backgroundColor: "#f0fbf9",
                  color: "#004141",
                  border: "1px solid #b2e5de",
                  fontWeight: 500,
                }}
              />
            ))}
          </Stack>
        </div>
      )}

      {keyObjectives && keyObjectives.length > 0 && (
        <div className={styles.section}>
          <Typography className={styles.sectionLabel}>Key Objectives</Typography>
          <Stack spacing={1}>
            {keyObjectives.map((objective: string, idx: number) => (
              <div key={idx} className={styles.objectiveItem}>
                <span className={styles.bulletDot} />
                <Typography className={styles.objectiveText}>
                  {objective}
                </Typography>
              </div>
            ))}
          </Stack>
        </div>
      )}

      {reasoning && (
        <div className={styles.section}>
          <Typography className={styles.sectionLabel}>
            Template Selection Reason
          </Typography>
          <div className={styles.reasoningBox}>
            <Typography className={styles.reasoningText}>{reasoning}</Typography>
          </div>
        </div>
      )}

      <div className={styles.actionButtons}>
        <Button
          variant="contained"
          onClick={() => onSend("confirm")}
          disabled={isDisabled}
          sx={{
            textTransform: "none",
            backgroundColor: "#00a888",
            "&:hover": { backgroundColor: "#00947a" },
            "&:disabled": { backgroundColor: "#ccc" },
          }}
        >
          Confirm & Proceed
        </Button>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => onSend("I need to make corrections")}
          disabled={isDisabled}
          sx={{
            textTransform: "none",
            borderColor: "#b2e5de",
            color: "#004141",
            "&:hover": {
              borderColor: "#00a888",
              backgroundColor: "#f0fbf9",
            },
            "&:disabled": { backgroundColor: "#ccc", borderColor: "#ccc" },
          }}
        >
          Make Corrections
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// DRAFT READY VIEW - Step 4/5: Draft generated with download
// ============================================================================
function DraftReadyView({
  data,
  result,
}: {
  data: any;
  result: any;
}): JSX.Element {
  const [expanded, setExpanded] = useState(false);

  const projectName = data?.project_name || result?.project_name || "";
  const parties = data?.parties || result?.parties || [];
  const governingLaw =
    data?.governing_law || result?.governing_law || data?.jurisdiction || "";
  const downloadUrl = data?.download_url || result?.download_url || "";
  const rawMessage = data?.message || result?.message || "";
  const templateType = data?.template_type || result?.template_type || "";
  const draftPreview = data?.draft_preview || result?.draft_preview || "";

  // Parse message to extract sources and SharePoint link for citation block
  const { mainContent, playbook, sources, sourcesNotConfigured, sharePointUrl } =
    parsePlaybookAndSources(rawMessage);

  const handleDownload = () => {
    if (downloadUrl) {
      window.open(downloadUrl, "_blank");
    }
  };

  const handleOpenSharePoint = () => {
    if (sharePointUrl) {
      window.open(sharePointUrl, "_blank");
    }
  };

  const handlePreview = () => {
    if (downloadUrl) {
      // Use Microsoft Office Online viewer for DOCX files
      // Add wdAllowInteractivity=False to ensure full document rendering
      const encodedUrl = encodeURIComponent(downloadUrl);
      const viewerUrl = `https://view.officeapps.live.com/op/view.aspx?src=${encodedUrl}&action=embedview&wdAllowInteractivity=False`;
      window.open(viewerUrl, "_blank");
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.sectionHeader}>
        <DescriptionIcon sx={{ color: "#00a888", fontSize: 20 }} />
        <Typography className={styles.sectionTitle}>
          Your MoU Draft is Ready
        </Typography>
      </div>

      {mainContent && (
        <div className={styles.summaryBox}>
          <MarkdownRenderer content={mainContent} />
        </div>
      )}

      {/* Citation block for sources */}
      <CitationBlock
        playbook={playbook}
        sources={sources}
        sourcesNotConfigured={sourcesNotConfigured}
      />

      <div className={styles.detailsGrid}>
        {projectName && (
          <div className={styles.detailItem}>
            <Typography className={styles.detailLabel}>Project</Typography>
            <Typography className={styles.detailValue}>{projectName}</Typography>
          </div>
        )}

        {parties.length > 0 && (
          <div className={styles.detailItem}>
            <Typography className={styles.detailLabel}>Parties</Typography>
            <Typography className={styles.detailValue}>
              {parties.join(" and ")}
            </Typography>
          </div>
        )}

        {governingLaw && (
          <div className={styles.detailItem}>
            <Typography className={styles.detailLabel}>Governing Law</Typography>
            <Typography className={styles.detailValue}>{governingLaw}</Typography>
          </div>
        )}

        {templateType && (
          <div className={styles.detailItem}>
            <Typography className={styles.detailLabel}>Template</Typography>
            <Typography className={styles.detailValue}>{templateType}</Typography>
          </div>
        )}
      </div>

      {draftPreview && (
        <div className={styles.section}>
          <div
            className={styles.draftHeader}
            onClick={() => setExpanded(!expanded)}
            style={{ cursor: "pointer" }}
          >
            <Typography className={styles.sectionLabel}>
              Draft Preview
            </Typography>
            {expanded ? (
              <ExpandLessIcon sx={{ color: "#004141" }} />
            ) : (
              <ExpandMoreIcon sx={{ color: "#004141" }} />
            )}
          </div>
          {expanded && (
            <div className={styles.draftPreviewBox}>
              <pre className={styles.draftText}>{draftPreview}</pre>
            </div>
          )}
        </div>
      )}

      <div className={styles.actionButtons}>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          disabled={!downloadUrl}
          sx={{
            textTransform: "none",
            backgroundColor: "#00a888",
            "&:hover": { backgroundColor: "#00947a" },
          }}
        >
          Download MoU DOCX
        </Button>
        <Button
          variant="outlined"
          startIcon={<VisibilityIcon />}
          onClick={handlePreview}
          disabled={!downloadUrl}
          sx={{
            textTransform: "none",
            borderColor: "#00a888",
            color: "#004141",
            "&:hover": {
              borderColor: "#00947a",
              backgroundColor: "#f0fbf9",
            },
          }}
        >
          Preview Document
        </Button>
        {sharePointUrl && (
          <Button
            variant="outlined"
            startIcon={<FolderSharedIcon />}
            onClick={handleOpenSharePoint}
            sx={{
              textTransform: "none",
              borderColor: "#0078d4",
              color: "#0078d4",
              "&:hover": {
                borderColor: "#106ebe",
                backgroundColor: "#f0f6fc",
              },
            }}
          >
            Open in SharePoint
          </Button>
        )}
      </div>

      <Typography className={styles.footerNote}>
        Please review and share with the legal team for sign-off.
      </Typography>
    </div>
  );
}

// ============================================================================
// FALLBACK VIEW - Generic message display
// ============================================================================
function FallbackView({
  data,
  message,
}: {
  data: any;
  message: string;
}): JSX.Element {
  const rawMessage =
    message || data?.response || data?.answer || JSON.stringify(data, null, 2);

  // Parse message to extract playbook/sources for citation block (if present)
  const isStringMessage = typeof rawMessage === "string";
  const { mainContent, playbook, sources, sourcesNotConfigured } = isStringMessage
    ? parsePlaybookAndSources(rawMessage)
    : { mainContent: rawMessage, playbook: null, sources: null, sourcesNotConfigured: false };

  return (
    <div className={styles.container}>
      <div className={styles.questionBox}>
        <div className={styles.questionIcon}>
          <DescriptionIcon sx={{ color: "#00a888", fontSize: 18 }} />
        </div>
        <div className={styles.questionContent}>
          {isStringMessage ? (
            <MarkdownRenderer content={mainContent} />
          ) : (
            <pre style={{ fontSize: "12px", overflow: "auto" }}>
              {JSON.stringify(mainContent, null, 2)}
            </pre>
          )}
        </div>
      </div>

      {/* Citation block for playbook/sources */}
      <CitationBlock
        playbook={playbook}
        sources={sources}
        sourcesNotConfigured={sourcesNotConfigured}
      />
    </div>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

type ConversationAction = {
  label: string;
  message: string;
  variant?: "contained" | "outlined";
};

function getConversationActions(question?: string): ConversationAction[] {
  if (!question) return [];

  const lower = question.toLowerCase();

  // Confirmation prompts
  if (
    lower.includes("confirm") ||
    lower.includes("type 'confirm'") ||
    lower.includes("type confirm")
  ) {
    return [
      { label: "Confirm", message: "confirm", variant: "contained" },
      { label: "Make Corrections", message: "I need to make corrections" },
    ];
  }

  // Cancel/exit prompts
  if (lower.includes("cancel") || lower.includes("exit")) {
    return [
      { label: "Cancel", message: "cancel" },
      { label: "Continue", message: "continue", variant: "contained" },
    ];
  }

  // Done/finish prompts - no button needed, input will be disabled when workflow completes
  // (removed "Done" button as workflow auto-completes when draft is ready)

  return [];
}
